import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useAuth } from 'shared/context/AuthContext';
import API from 'shared/api';
import {
  FiSearch,
  FiX,
  FiPhone,
  FiMail,
  FiMapPin,
  FiClock,
  FiShoppingBag,
  FiDollarSign,
  FiPhoneCall,
  FiHome,
  FiArrowLeft,
  FiEdit2,
  FiTrash2,
  FiUserPlus,
  FiUser,
  FiLock,
  FiKey,
  FiBriefcase,
  FiCamera,
  FiChevronDown,
  FiChevronUp,
  FiChevronRight,
  FiPlus,
  FiTruck,
  FiCalendar,
  FiPackage,
  FiCheckCircle,
  FiXCircle,
  FiFileText,
  FiSend,
  FiRefreshCw,
} from 'react-icons/fi';
import { MapContainer, TileLayer, Marker, Popup, useMapEvents } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import VisorComprobante from 'shared/components/VisorComprobante';
import { getImagenUrl } from 'shared/api';
import { fmtMonto } from 'shared/format';
import ClientesOrdenCompraModal from './ClientesOrdenCompraModal';

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

const ESTADO_LABELS = {
  NUEVO: 'Nuevo',
  ESPERANDO_NOMBRE: 'Esperando nombre',
  ESPERANDO_CEDULA: 'Esperando cedula',
  ESPERANDO_TELEFONO: 'Esperando teléfono',
  ESPERANDO_UBICACION: 'Esperando ubicación',
  CONVERSANDO: 'Conversando',
};
const ESTADO_COLORS = {
  NUEVO: '#6b7280',
  ESPERANDO_NOMBRE: '#f59e0b',
  ESPERANDO_CEDULA: '#f59e0b',
  ESPERANDO_TELEFONO: '#f59e0b',
  ESPERANDO_UBICACION: '#3b82f6',
  CONVERSANDO: '#10b981',
};
const TIPO_LABELS = { natural: 'Natural', juridico: 'Juridico', corporativo: 'Corporativo' };

const ESTADO_PEDIDO = {
  pendiente: 'Pendiente',
  en_revision: 'En revision',
  aprobado: 'Aprobado',
  esperando_pago: 'Pago pendiente',
  pago_en_verificacion: 'Verificando',
  pagado: 'Pagado',
  armado: 'Armando',
  listo: 'Listo',
  despachado: 'Despachado',
  en_camino: 'En camino',
  llegada: 'Llegada',
  entregado: 'Entregado',
  rechazado: 'Rechazado',
  cancelado: 'Cancelado',
  credito_pagado: 'Credito pagado',
};
const PEDIDO_COLOR = {
  pendiente: '#FF9500',
  en_revision: '#FF9500',
  aprobado: '#2563eb',
  esperando_pago: '#FF9500',
  pago_en_verificacion: '#FF9500',
  pagado: '#34a853',
  armado: '#ff6d00',
  listo: '#2563eb',
  despachado: '#00bcd4',
  en_camino: '#00bcd4',
  llegada: '#009688',
  entregado: '#34a853',
  rechazado: '#ea4335',
  cancelado: '#9e9e9e',
  credito_pagado: '#34a853',
};

const ESTADO_COMP_LABELS = {
  nuevo: 'Nuevo',
  registrando: 'Registrando',
  activo: 'Activo',
  con_deuda: 'Con deuda',
  vip: 'VIP',
  inactivo: 'Inactivo',
  moroso: 'Moroso',
};
const ESTADO_COMP_COLORS = {
  nuevo: '#6b7280',
  registrando: '#6b7280',
  activo: '#34a853',
  con_deuda: '#f59e0b',
  vip: '#8b5cf6',
  inactivo: '#9e9e9e',
  moroso: '#ea4335',
};

const emptyForm = {
  usuario: '',
  clave: '',
  nombre: '',
  apellido: '',
  telefono: '',
  cedula: '',
  email: '',
  tipo: 'natural',
  rif: '',
  lat: '',
  lng: '',
  direccion: '',
  nombre_empresa: '',
  telefono2: '',
  representante_nombre: '',
  representante_cedula: '',
  representante_telefono: '',
  observaciones: '',
  sedes: [],
};
const DRAFT_KEY = 'agorax_draft_cliente';
const RIF_REGEX = /^[JGCEV]-\d{5,9}-\d$/;

function esCedulaValida(cedula) {
  const limpia = String(cedula || '')
    .trim()
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, '');
  return /^[VEGJP]?\d{6,10}$/.test(limpia);
}

function generatePassword(len = 12) {
  const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789!@#$';
  let p = '';
  for (let i = 0; i < len; i++) p += chars[Math.floor(Math.random() * chars.length)];
  return p;
}

export default function Clientes() {
  const { tienePermiso, tieneVisibilidad } = useAuth();
  const v = (c) => tieneVisibilidad(c);
  const [clientes, setClientes] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [busqueda, setBusqueda] = useState('');
  const [filtroTipo, setFiltroTipo] = useState('todos');
  const [selectedId, setSelectedId] = useState(null);
  const [clienteDetalle, setClienteDetalle] = useState(null);
  const [cargandoDetalle, setCargandoDetalle] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [showForm, setShowForm] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [formSaving, setFormSaving] = useState(false);
  const [mostrarSolicitudes, setMostrarSolicitudes] = useState(false);
  const [tabSolicitud, setTabSolicitud] = useState('pendiente');
  const [solicitudes, setSolicitudes] = useState([]);
  const [cargandoSolicitudes, setCargandoSolicitudes] = useState(false);
  const [rechazando, setRechazando] = useState(null);
  const [motivoRechazo, setMotivoRechazo] = useState('');
  const [ordenCompraCliente, setOrdenCompraCliente] = useState(null);
  const [tieneErp, setTieneErp] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [syncErpCliente, setSyncErpCliente] = useState(null);
  const puedeSincronizar = tienePermiso('clientes.gestionar');
  const searchRef = useRef(null);

  // Atajo de teclado "/" para enfocar la busqueda de clientes.
  useEffect(() => {
    const h = (e) => {
      if (e.key !== '/' || e.ctrlKey || e.metaKey || e.altKey) return;
      const tag = (e.target?.tagName || '').toLowerCase();
      if (tag === 'input' || tag === 'textarea' || tag === 'select') return;
      e.preventDefault();
      searchRef.current?.focus();
    };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, []);

  // Reabrir el formulario de cliente con el borrador pendiente tras una recarga
  const draftRehidratadoRef = useRef(false);
  useEffect(() => {
    if (draftRehidratadoRef.current) return;
    draftRehidratadoRef.current = true;
    try {
      const raw = localStorage.getItem(DRAFT_KEY);
      if (!raw) return;
      const draft = { ...emptyForm, ...JSON.parse(raw) };
      const tieneContenido = Object.keys(draft).some(
        (k) => k !== 'tipo' && draft[k] && String(draft[k]).trim() !== ''
      );
      if (tieneContenido) {
        setForm(draft);
        setEditMode(false);
        setShowForm(true);
      }
    } catch (e) {
      /* noop */
    }
  }, []);

  // Persistir borrador SOLO mientras se crea un cliente nuevo (no en edicion)
  useEffect(() => {
    if (showForm && !editMode) {
      try {
        localStorage.setItem(DRAFT_KEY, JSON.stringify(form));
      } catch (e) {
        /* noop */
      }
    }
  }, [form, showForm, editMode]);

  useEffect(() => {
    const h = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', h);
    return () => window.removeEventListener('resize', h);
  }, []);

  const cargarClientes = useCallback(async () => {
    try {
      const r = await API.get('/clientes/listar');
      if (r.data.status === 'success') {
        setClientes(r.data.data || []);
        setTieneErp(!!r.data.tiene_erp);
      }
    } catch (e) {
      console.error(e);
    } finally {
      setCargando(false);
    }
  }, []);

  useEffect(() => {
    if (tienePermiso('clientes.ver')) cargarClientes();
  }, [cargarClientes, tienePermiso]);

  useEffect(() => {
    if (!selectedId) {
      setClienteDetalle(null);
      return;
    }
    let cancelled = false;
    setCargandoDetalle(true);
    (async () => {
      try {
        const r = await API.get(`/clientes/${selectedId}`);
        if (!cancelled && r.data.status === 'success') setClienteDetalle(r.data.data);
      } catch (e) {
        console.error(e);
      } finally {
        if (!cancelled) setCargandoDetalle(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [selectedId, refreshKey]);

  const esDeudor = (c) => (c.pendiente_credito || 0) > 0;
  const esTienda = (c) => c.origen === 'tienda';

  const filtrados = useMemo(() => {
    let list = clientes;
    if (busqueda) {
      const q = busqueda.toLowerCase();
      list = list.filter((c) =>
        [c.nombre, c.apellido, c.telefono, c.cedula, c.email, c.usuario]
          .filter(Boolean)
          .some((v) => (v || '').toLowerCase().includes(q))
      );
    }
    if (filtroTipo === 'advin') list = list.filter((c) => c.erp_codigo);
    else if (filtroTipo === 'deudores') list = list.filter(esDeudor);
    else if (filtroTipo === 'solventes')
      list = list.filter((c) => c.usuario && !esDeudor(c));
    else if (filtroTipo === 'juridico') list = list.filter((c) => c.tipo === 'juridico');
    else if (filtroTipo === 'tienda') list = list.filter(esTienda);
    else list = list.filter((c) => c.usuario || esTienda(c) || c.erp_codigo);
    return list;
  }, [clientes, busqueda, filtroTipo]);

  const statsGlobal = useMemo(
    () => ({
      total: clientes.filter((c) => c.usuario || esTienda(c) || c.erp_codigo).length,
      tienda: clientes.filter(esTienda).length,
      advin: clientes.filter((c) => c.erp_codigo).length,
      deudores: clientes.filter(esDeudor).length,
      solventes: clientes.filter((c) => c.usuario && !esDeudor(c)).length,
      juridicos: clientes.filter((c) => c.tipo === 'juridico').length,
    }),
    [clientes]
  );

  // Filtros con su contador en un solo nivel (sustituye a los badges duplicados).
  const filtros = [
    { k: 'todos', l: 'Todos', n: statsGlobal.total },
    ...(tieneErp ? [{ k: 'advin', l: 'ADVIN', n: statsGlobal.advin }] : []),
    { k: 'tienda', l: 'Tienda', n: statsGlobal.tienda },
    { k: 'deudores', l: 'Deudores', n: statsGlobal.deudores },
    { k: 'solventes', l: 'Solventes', n: statsGlobal.solventes },
    { k: 'juridico', l: 'Jurídicos', n: statsGlobal.juridicos },
  ];

  const handleSave = async () => {
    if (!editMode && form.tipo === 'natural' && !esCedulaValida(form.cedula)) {
      alert('La cédula no es válida. Debe tener entre 6 y 10 dígitos.');
      return;
    }
    setFormSaving(true);
    try {
      const sucursales = (form.sedes || [])
        .filter((s) => s && (s.nombre || '').trim())
        .map((s) => ({
          nombre: (s.nombre || '').trim(),
          direccion: (s.direccion || '').trim(),
          telefono: (s.telefono || '').trim(),
          email: (s.email || '').trim(),
          representante: (s.representante || '').trim(),
          representante_cedula: (s.representante_cedula || '').trim(),
          representante_telefono: (s.representante_telefono || '').trim(),
          rif: (s.rif || '').trim(),
        }));
      const payload = {
        ...form,
        lat: parseFloat(form.lat) || null,
        lng: parseFloat(form.lng) || null,
        sedes: sucursales,
      };
      if (editMode) {
        const updates = {};
        Object.entries(payload).forEach(([k, v]) => {
          if (v !== '' && v !== null && v !== undefined && v !== emptyForm[k]) updates[k] = v;
        });
        await API.put(`/clientes/${clienteDetalle.id}/modificar`, updates);
      } else {
        await API.post('/clientes/crear', payload);
      }
      setShowForm(false);
      try {
        localStorage.removeItem(DRAFT_KEY);
      } catch (e) {
        /* noop */
      }
      cargarClientes();
      if (editMode) setSelectedId(null);
    } catch (e) {
      const msg =
        e?.response?.status === 409
          ? 'El usuario ya esta en uso'
          : e?.response?.data?.detail || 'Error al guardar';
      alert(msg);
    }
    setFormSaving(false);
  };

  const openCreate = () => {
    setEditMode(false);
    let draft = emptyForm;
    try {
      const raw = localStorage.getItem(DRAFT_KEY);
      if (raw) draft = { ...emptyForm, ...JSON.parse(raw) };
    } catch (e) {
      /* noop */
    }
    setForm(draft);
    setShowForm(true);
  };
  const openEdit = (data) => {
    setEditMode(true);
    try {
      localStorage.removeItem(DRAFT_KEY);
    } catch (e) {
      /* noop */
    }
    const normalized = {};
    Object.keys(emptyForm).forEach((k) => {
      normalized[k] = data[k] != null ? data[k] : emptyForm[k];
    });
    setForm({
      ...normalized,
      nombre_empresa: data.nombre_empresa || data.nombre || '',
      clave: '',
      sedes: (data.sedes || []).map((s) => ({
        nombre: s.nombre || '',
        direccion: s.direccion || '',
        telefono: s.telefono || '',
        email: s.email || '',
        representante: s.representante || '',
        representante_cedula: s.representante_cedula || '',
        representante_telefono: s.representante_telefono || '',
        rif: s.rif || '',
      })),
    });
    setShowForm(true);
  };

  const deleteClient = async (data) => {
    if (!window.confirm(`Eliminar a ${data.nombre || 'este cliente'}?`)) return;
    try {
      await API.delete(`/clientes/${data.id}/eliminar`);
      cargarClientes();
      setSelectedId(null);
    } catch (e) {
      alert(e?.response?.data?.detail || 'Error al eliminar');
    }
  };

  const handleSelect = (id) => {
    if (id === selectedId) {
      setSelectedId(null);
    } else {
      setSelectedId(id);
    }
  };

  const cargarSolicitudes = useCallback(async () => {
    setCargandoSolicitudes(true);
    try {
      const r = await API.get('/clientes/solicitudes', { params: { estado: tabSolicitud } });
      if (r.data.status === 'success') setSolicitudes(r.data.data || []);
    } catch (e) {
      console.error(e);
    } finally {
      setCargandoSolicitudes(false);
    }
  }, [tabSolicitud]);

  useEffect(() => {
    if (mostrarSolicitudes && tienePermiso('clientes.ver')) cargarSolicitudes();
  }, [mostrarSolicitudes, cargarSolicitudes, tienePermiso]);

  const aprobarCliente = async (data) => {
    if (!window.confirm(`Aprobar a ${data.nombre || 'este cliente'}?`)) return;
    try {
      await API.put(`/clientes/${data.id}/aprobar`);
      cargarClientes();
      cargarSolicitudes();
      setSelectedId(null);
    } catch (e) {
      alert(e?.response?.data?.detail || 'Error al aprobar');
    }
  };

  const confirmarRechazo = async () => {
    if (!rechazando) return;
    if (!motivoRechazo.trim()) {
      alert('Debe indicar un motivo de rechazo');
      return;
    }
    try {
      await API.put(`/clientes/${rechazando.id}/rechazar`, { motivo: motivoRechazo.trim() });
      setRechazando(null);
      setMotivoRechazo('');
      cargarClientes();
      cargarSolicitudes();
      setSelectedId(null);
    } catch (e) {
      alert(e?.response?.data?.detail || 'Error al rechazar');
    }
  };

  const desincronizarErp = async (data) => {
    if (!window.confirm(`Desincronizar a ${data.nombre || 'este cliente'} de ADVIN?`)) return;
    try {
      await API.post(`/clientes/${data.id}/desincronizar-erp`);
      cargarClientes();
      setRefreshKey((k) => k + 1);
    } catch (e) {
      alert(e?.response?.data?.detail || 'Error al desincronizar');
    }
  };

  const confirmarSincronizacionErp = async (clienteId, erpCodigo) => {
    try {
      await API.post(`/clientes/${clienteId}/sincronizar-erp`, { erp_codigo: erpCodigo });
      setSyncErpCliente(null);
      cargarClientes();
      setRefreshKey((k) => k + 1);
    } catch (e) {
      alert(e?.response?.data?.detail || 'Error al sincronizar');
    }
  };

  return (
    <div
      style={{ display: 'flex', height: '100%', overflow: 'hidden', background: 'var(--agx-bg)' }}
    >
      {/* List panel */}
      <div
        style={{
          width: isMobile ? (selectedId ? 0 : '100%') : `min(380px, 40%)`,
          flexShrink: 0,
          display: isMobile && selectedId ? 'none' : 'flex',
          flexDirection: 'column',
          borderRight: isMobile ? 'none' : '1px solid var(--agx-border)',
          background: 'var(--agx-surface)',
        }}
      >
        <div style={{ padding: '16px', borderBottom: '1px solid var(--agx-border)' }}>
          <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
            <div
              style={{
                flex: 1,
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                padding: '8px 12px',
                borderRadius: 10,
                background: 'var(--agx-bg2)',
                border: '1px solid var(--agx-border)',
              }}
            >
              <FiSearch size={14} color="var(--agx-faint)" />
              <input
                ref={searchRef}
                value={busqueda}
                onChange={(e) => setBusqueda(e.target.value)}
                placeholder="Nombre, cédula o teléfono"
                style={{
                  flex: 1,
                  border: 'none',
                  background: 'transparent',
                  fontSize: 13,
                  outline: 'none',
                  color: 'var(--agx-text)',
                  fontFamily: 'inherit',
                }}
              />
              {busqueda ? (
                <button onClick={() => setBusqueda('')} style={S.clr} title="Limpiar">
                  <FiX size={12} />
                </button>
              ) : (
                <span
                  style={{
                    fontSize: 10,
                    fontWeight: 600,
                    color: 'var(--agx-faint)',
                    border: '1px solid var(--agx-border)',
                    borderRadius: 4,
                    padding: '0 5px',
                    lineHeight: '16px',
                  }}
                >
                  /
                </span>
              )}
            </div>
            <button onClick={openCreate} style={S.createBtn} title="Nuevo cliente">
              <FiUserPlus size={14} />
            </button>
          </div>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {filtros.map((f) => (
              <button
                key={f.k}
                onClick={() => setFiltroTipo(f.k)}
                style={{
                  ...S.filterBtn,
                  background: filtroTipo === f.k ? 'var(--agx-pri2)' : 'var(--agx-bg2)',
                  color: filtroTipo === f.k ? '#fff' : 'var(--agx-text)',
                  borderColor: filtroTipo === f.k ? 'var(--agx-pri2)' : 'var(--agx-border)',
                }}
              >
                {f.l} <span style={{ opacity: 0.65, fontWeight: 500 }}>{f.n}</span>
              </button>
            ))}
            <span style={{ width: 1, alignSelf: 'stretch', background: 'var(--agx-border)' }} />
            <button
              onClick={() => {
                setMostrarSolicitudes(!mostrarSolicitudes);
                setSelectedId(null);
              }}
              style={{
                ...S.filterBtn,
                background: mostrarSolicitudes ? 'var(--agx-pri2)' : 'var(--agx-bg2)',
                color: mostrarSolicitudes ? '#fff' : 'var(--agx-faint)',
                borderColor: mostrarSolicitudes ? 'var(--agx-pri2)' : 'var(--agx-border)',
              }}
            >
              Solicitudes
            </button>
          </div>
        </div>
        {mostrarSolicitudes && (
          <div style={{ display: 'flex', borderBottom: '1px solid var(--agx-border)' }}>
            {[
              { k: 'pendiente', l: 'Pendientes' },
              { k: 'rechazado', l: 'Rechazadas' },
            ].map((t) => (
              <button
                key={t.k}
                onClick={() => setTabSolicitud(t.k)}
                style={{
                  flex: 1,
                  padding: '9px',
                  border: 'none',
                  background: tabSolicitud === t.k ? 'var(--agx-blue-bg)' : 'transparent',
                  color: tabSolicitud === t.k ? 'var(--agx-pri2)' : 'var(--agx-faint)',
                  fontWeight: tabSolicitud === t.k ? 700 : 500,
                  fontSize: 12,
                  cursor: 'pointer',
                  borderBottom:
                    tabSolicitud === t.k ? '2px solid var(--agx-pri2)' : '2px solid transparent',
                  fontFamily: 'inherit',
                }}
              >
                {t.l}
              </button>
            ))}
          </div>
        )}
        <div style={{ flex: 1, overflowY: 'auto' }}>
          {mostrarSolicitudes ? (
            cargandoSolicitudes ? (
              <div style={S.loading}>
                <div style={S.spin} />
              </div>
            ) : solicitudes.length === 0 ? (
              <div
                style={{
                  padding: 24,
                  textAlign: 'center',
                  color: 'var(--agx-faint)',
                  fontSize: 13,
                }}
              >
                No hay solicitudes {tabSolicitud === 'pendiente' ? 'pendientes' : 'rechazadas'}.
              </div>
            ) : (
              solicitudes.map((c) => {
                const sel = c.id === selectedId;
                return (
                  <div
                    key={c.id}
                    onClick={() => handleSelect(c.id)}
                    style={{
                      ...S.clientRow,
                      background: sel ? 'var(--agx-blue-bg)' : 'transparent',
                      borderLeft: sel ? '3px solid var(--agx-pri2)' : '3px solid transparent',
                    }}
                  >
                    <div
                      style={{
                        width: 40,
                        height: 40,
                        borderRadius: '50%',
                        background: tabSolicitud === 'pendiente' ? '#f59e0b' : '#ea4335',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: '#fff',
                        fontWeight: 700,
                        fontSize: 15,
                        flexShrink: 0,
                      }}
                    >
                      {(c.nombre || '?')[0].toUpperCase()}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <span
                        style={{
                          fontSize: 13,
                          fontWeight: 600,
                          color: 'var(--agx-text)',
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}
                      >
                        {[c.nombre, c.apellido].filter(Boolean).join(' ') || 'Sin nombre'}
                      </span>
                      <div style={{ display: 'flex', gap: 8, marginTop: 2, flexWrap: 'wrap' }}>
                        {v('vis.clientes.telefono') && c.telefono && (
                          <span style={{ fontSize: 11, color: 'var(--agx-muted)' }}>
                            {c.telefono}
                          </span>
                        )}
                        {c.cedula && (
                          <span style={{ fontSize: 11, color: 'var(--agx-muted)' }}>
                            CI: {c.cedula}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })
            )
          ) : cargando ? (
            <div style={S.loading}>
              <div style={S.spin} />
            </div>
          ) : filtrados.length === 0 ? (
            <div
              style={{
                padding: '32px 20px',
                textAlign: 'center',
                color: 'var(--agx-faint)',
                fontSize: 13,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 10,
              }}
            >
              <FiSearch size={24} />
              <span>
                {busqueda ? `Sin resultados para "${busqueda}"` : 'No hay clientes para este filtro'}
              </span>
              {(busqueda || filtroTipo !== 'todos') && (
                <button
                  onClick={() => {
                    setBusqueda('');
                    setFiltroTipo('todos');
                  }}
                  style={{
                    ...S.filterBtn,
                    background: 'var(--agx-bg2)',
                    color: 'var(--agx-pri2)',
                    borderColor: 'var(--agx-border)',
                  }}
                >
                  Limpiar filtros
                </button>
              )}
            </div>
          ) : (
            filtrados.map((c) => {
              const sel = c.id === selectedId;
              const ecomp = c.estado_comp || 'nuevo';
              const ecolor = ESTADO_COMP_COLORS[ecomp] || '#6b7280';
              return (
                <div
                  key={c.id}
                  onClick={() => handleSelect(c.id)}
                  style={{
                    ...S.clientRow,
                    background: sel ? 'var(--agx-blue-bg)' : 'transparent',
                    borderLeft: sel ? '3px solid var(--agx-pri2)' : '3px solid transparent',
                  }}
                >
                  <div
                    style={{
                      width: 40,
                      height: 40,
                      borderRadius: '50%',
                      background: ecolor,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: '#fff',
                      fontWeight: 700,
                      fontSize: 15,
                      flexShrink: 0,
                    }}
                  >
                    {(c.nombre || '?')[0].toUpperCase()}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <span
                        style={{
                          fontSize: 13,
                          fontWeight: 600,
                          color: 'var(--agx-text)',
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}
                      >
                        {[c.nombre, c.apellido].filter(Boolean).join(' ') || 'Sin nombre'}
                      </span>
                      <span
                        style={{
                          padding: '1px 6px',
                          borderRadius: 4,
                          fontSize: 9,
                          fontWeight: 600,
                          background: ecolor + '1a',
                          color: ecolor,
                        }}
                      >
                        {ESTADO_COMP_LABELS[ecomp]}
                      </span>
                      {c.erp_codigo && (
                        <span
                          title="Sincronizado con ADVIN"
                          style={{
                            padding: '1px 6px',
                            borderRadius: 4,
                            fontSize: 9,
                            fontWeight: 700,
                            letterSpacing: '0.3px',
                            background: '#e0f2fe',
                            color: '#0369a1',
                          }}
                        >
                          ADVIN
                        </span>
                      )}
                      {esTienda(c) && (
                        <span
                          title="Cliente registrado desde la tienda"
                          style={{
                            padding: '1px 6px',
                            borderRadius: 4,
                            fontSize: 9,
                            fontWeight: 700,
                            letterSpacing: '0.3px',
                            background: '#e0f2fe',
                            color: '#0ea5e9',
                          }}
                        >
                          TIENDA
                        </span>
                      )}
                    </div>
                    <div style={{ display: 'flex', gap: 8, marginTop: 2, flexWrap: 'wrap' }}>
                      {v('vis.clientes.telefono') && c.telefono && (
                        <span style={{ fontSize: 11, color: 'var(--agx-muted)' }}>
                          {c.telefono}
                        </span>
                      )}
                      {c.pendiente_credito > 0 && (
                        <span
                          style={{
                            fontSize: 10,
                            fontWeight: 600,
                            padding: '1px 6px',
                            borderRadius: 4,
                            background: '#fef2f2',
                            color: '#ea4335',
                          }}
                        >
                          Debe ${fmtMonto(c.pendiente_credito)}
                        </span>
                      )}
                      {(c.total_pedidos || 0) > 0 && (
                        <span
                          style={{
                            fontSize: 10,
                            fontWeight: 600,
                            padding: '1px 6px',
                            borderRadius: 4,
                            background: 'var(--agx-bg2)',
                            color: 'var(--agx-pri2)',
                          }}
                        >
                          {c.total_pedidos} pedido{c.total_pedidos !== 1 ? 's' : ''}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>

      {/* Detail panel */}
      <div
        style={{
          flex: 1,
          display: isMobile && !selectedId ? 'none' : 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        {selectedId ? (
          <DetailContent
            v={v}
            data={clienteDetalle}
            cargando={cargandoDetalle}
            onClose={() => setSelectedId(null)}
            onEdit={openEdit}
            onDelete={deleteClient}
            onAprobar={aprobarCliente}
            onRechazar={(data) => {
              setRechazando(data);
              setMotivoRechazo('');
            }}
            onCargarOrdenCompra={(data) => setOrdenCompraCliente(data)}
            isMobile={isMobile}
            puedeSincronizar={puedeSincronizar}
            onSincronizar={(data) => setSyncErpCliente(data)}
            onDesincronizar={desincronizarErp}
          />
        ) : (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              height: '100%',
              color: 'var(--agx-faint)',
              flexDirection: 'column',
              gap: 8,
              fontSize: 13,
            }}
          >
            <FiUser size={32} />
            <span>Selecciona un cliente para ver su detalle</span>
          </div>
        )}
      </div>

      {showForm && (
        <FormModal
          form={form}
          setForm={setForm}
          editMode={editMode}
          onSave={handleSave}
          saving={formSaving}
          onClose={() => setShowForm(false)}
        />
      )}

      {ordenCompraCliente && (
        <ClientesOrdenCompraModal
          cliente={ordenCompraCliente}
          onClose={() => setOrdenCompraCliente(null)}
        />
      )}

      {syncErpCliente && (
        <SyncErpModal
          cliente={syncErpCliente}
          onClose={() => setSyncErpCliente(null)}
          onConfirm={confirmarSincronizacionErp}
        />
      )}

      {rechazando && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            padding: 20,
          }}
        >
          <div
            style={{
              background: 'var(--agx-surface)',
              borderRadius: 16,
              padding: 24,
              width: '100%',
              maxWidth: 420,
            }}
          >
            <h3 style={{ margin: '0 0 4px', fontSize: 18, fontWeight: 700, color: 'var(--agx-text)' }}>
              Rechazar solicitud
            </h3>
            <p style={{ margin: '0 0 14px', fontSize: 13, color: 'var(--agx-muted)' }}>
              {rechazando.nombre} {rechazando.apellido || ''}
            </p>
            <textarea
              value={motivoRechazo}
              onChange={(e) => setMotivoRechazo(e.target.value)}
              placeholder="Indica el motivo del rechazo"
              rows={3}
              style={{
                width: '100%',
                boxSizing: 'border-box',
                padding: '10px 12px',
                borderRadius: 10,
                border: '1px solid var(--agx-border)',
                background: 'var(--agx-input-bg)',
                color: 'var(--agx-text)',
                fontSize: 14,
                fontFamily: 'inherit',
                resize: 'vertical',
              }}
            />
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 }}>
              <button
                onClick={() => setRechazando(null)}
                style={{
                  padding: '9px 16px',
                  borderRadius: 10,
                  border: '1px solid var(--agx-border)',
                  background: 'var(--agx-surface)',
                  color: 'var(--agx-muted)',
                  cursor: 'pointer',
                  fontSize: 13,
                  fontWeight: 600,
                  fontFamily: 'inherit',
                }}
              >
                Cancelar
              </button>
              <button
                onClick={confirmarRechazo}
                style={{
                  padding: '9px 16px',
                  borderRadius: 10,
                  border: 'none',
                  background: '#ea4335',
                  color: '#fff',
                  cursor: 'pointer',
                  fontSize: 13,
                  fontWeight: 600,
                  fontFamily: 'inherit',
                }}
              >
                Rechazar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════════
   DETAIL CONTENT — 3 tabs: Info, Pedidos, Credito
   ══════════════════════════════════════════════════════════════════ */
function DetailContent({
  v,
  data,
  cargando,
  onClose,
  onEdit,
  onDelete,
  onAprobar,
  onRechazar,
  onCargarOrdenCompra,
  isMobile,
  puedeSincronizar,
  onSincronizar,
  onDesincronizar,
}) {
  const [tab, setTab] = useState('info');

  if (cargando || !data)
    return (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: 200,
          color: 'var(--agx-faint)',
        }}
      >
        Cargando...
      </div>
    );

  const st = data.stats || {};
  const nombreCompleto = [data.nombre, data.apellido].filter(Boolean).join(' ') || 'Sin nombre';

  const tabs = [
    { id: 'info', label: 'Info', Icon: FiUser },
    { id: 'pedidos', label: 'Pedidos', Icon: FiShoppingBag, badge: st.total_pedidos || 0 },
    { id: 'credito', label: 'Credito', Icon: FiDollarSign },
  ];

  const esPendiente = data.estado_aprobacion === 'pendiente';
  const esRechazado = data.estado_aprobacion === 'rechazado';

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
      {/* Header */}
      <div
        style={{
          padding: '16px 20px',
          background: 'var(--agx-surface)',
          borderBottom: '1px solid var(--agx-border)',
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, flex: 1 }}>
            {isMobile && (
              <button onClick={onClose} style={S.backBtn}>
                <FiArrowLeft size={16} />
              </button>
            )}
            <div
              style={{
                width: 44,
                height: 44,
                borderRadius: '50%',
                background: `linear-gradient(135deg, var(--agx-pri2), #60a5fa)`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#fff',
                fontWeight: 700,
                fontSize: 18,
                flexShrink: 0,
              }}
            >
              {(data.nombre || '?')[0].toUpperCase()}
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                <span style={{ fontSize: 16, fontWeight: 700, color: 'var(--agx-text)' }}>
                  {nombreCompleto}
                </span>
                <span
                  style={{
                    padding: '2px 8px',
                    borderRadius: 10,
                    fontSize: 10,
                    fontWeight: 600,
                    background: (ESTADO_COLORS[data.estado] || '#6b7280') + '18',
                    color: ESTADO_COLORS[data.estado] || '#6b7280',
                  }}
                >
                  {ESTADO_LABELS[data.estado] || data.estado}
                </span>
                {data.tipo === 'juridico' && (
                  <span
                    style={{
                      padding: '2px 8px',
                      borderRadius: 10,
                      fontSize: 9,
                      fontWeight: 600,
                      background: 'var(--agx-info-bg)',
                      color: '#a78bfa',
                    }}
                  >
                    {TIPO_LABELS.juridico}
                  </span>
                )}
                {data.erp_codigo && (
                  <span
                    title="Sincronizado con ADVIN"
                    style={{
                      padding: '2px 8px',
                      borderRadius: 10,
                      fontSize: 9,
                      fontWeight: 700,
                      letterSpacing: '0.3px',
                      background: '#e0f2fe',
                      color: '#0369a1',
                    }}
                  >
                    ADVIN
                  </span>
                )}
              </div>
              <div style={{ display: 'flex', gap: 10, marginTop: 4, flexWrap: 'wrap' }}>
                {v('vis.clientes.telefono') && data.telefono && (
                  <a
                    href={`tel:${data.telefono}`}
                    style={{
                      textDecoration: 'none',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 4,
                      fontSize: 11,
                      color: 'var(--agx-pri2)',
                      fontWeight: 500,
                    }}
                  >
                    <FiPhoneCall size={10} />
                    {data.telefono}
                  </a>
                )}
                {v('vis.clientes.email') && data.email && (
                  <a
                    href={`mailto:${data.email}`}
                    style={{
                      textDecoration: 'none',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 4,
                      fontSize: 11,
                      color: 'var(--agx-muted)',
                      fontWeight: 500,
                    }}
                  >
                    <FiMail size={10} />
                    {data.email}
                  </a>
                )}
              </div>
            </div>
          </div>
          <div style={{ display: 'flex', gap: 4, flexShrink: 0 }}>
            {data.tipo === 'corporativo' && data.puede_cargar_orden_compra && onCargarOrdenCompra && (
              <button
                onClick={() => onCargarOrdenCompra(data)}
                style={{ ...S.actionBtn, color: 'var(--agx-pri2)', width: 'auto', padding: '0 10px', gap: 6 }}
                title="Cargar orden de compra"
              >
                <FiFileText size={12} />
                Orden de compra
              </button>
            )}
            {puedeSincronizar && data.tiene_erp && !data.erp_codigo && onSincronizar && (
              <button
                onClick={() => onSincronizar(data)}
                style={{ ...S.actionBtn, color: '#0369a1', width: 'auto', padding: '0 10px', gap: 6 }}
                title="Sincronizar con ADVIN"
              >
                <FiRefreshCw size={12} />
                Sincronizar
              </button>
            )}
            {puedeSincronizar && data.erp_codigo && onDesincronizar && (
              <button
                onClick={() => onDesincronizar(data)}
                style={{ ...S.actionBtn, color: '#ea4335', width: 'auto', padding: '0 10px', gap: 6 }}
                title="Desincronizar de ADVIN"
              >
                <FiRefreshCw size={12} />
                Desincronizar
              </button>
            )}
            <button onClick={() => onEdit(data)} style={S.actionBtn}>
              <FiEdit2 size={12} />
            </button>
            <button onClick={() => onDelete(data)} style={{ ...S.actionBtn, color: '#ea4335' }}>
              <FiTrash2 size={12} />
            </button>
          </div>
        </div>
        {(esPendiente || esRechazado) && onAprobar && (
          <div
            style={{
              padding: '10px 20px',
              background: esPendiente ? 'var(--agx-amber-bg)' : 'var(--agx-red-bg)',
              borderTop: '1px solid var(--agx-border)',
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              flexWrap: 'wrap',
            }}
          >
            <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--agx-muted)', flex: 1, minWidth: 180 }}>
              {esPendiente
                ? 'Este cliente está pendiente de aprobación.'
                : `Solicitud rechazada${data.motivo_rechazo ? `: ${data.motivo_rechazo}` : '.'}`}
            </span>
            <button
              onClick={() => onAprobar(data)}
              style={{
                padding: '8px 16px',
                borderRadius: 8,
                border: 'none',
                background: '#34a853',
                color: '#fff',
                fontSize: 12,
                fontWeight: 700,
                cursor: 'pointer',
                fontFamily: 'inherit',
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
              }}
            >
              <FiCheckCircle size={14} /> Aprobar
            </button>
            {esPendiente && onRechazar && (
              <button
                onClick={() => onRechazar(data)}
                style={{
                  padding: '8px 16px',
                  borderRadius: 8,
                  border: 'none',
                  background: '#ea4335',
                  color: '#fff',
                  fontSize: 12,
                  fontWeight: 700,
                  cursor: 'pointer',
                  fontFamily: 'inherit',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 6,
                }}
              >
                <FiXCircle size={14} /> Rechazar
              </button>
            )}
          </div>
        )}
      </div>

      {/* Tabs */}
      <div
        style={{
          display: 'flex',
          borderBottom: '1px solid var(--agx-border)',
          background: 'var(--agx-surface)',
          flexShrink: 0,
        }}
      >
        {tabs.map((t) => {
          const active = tab === t.id;
          return (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              style={{
                flex: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 6,
                padding: '10px 16px',
                border: 'none',
                cursor: 'pointer',
                background: 'transparent',
                borderBottom: active ? '2px solid var(--agx-pri2)' : '2px solid transparent',
                color: active ? 'var(--agx-pri2)' : 'var(--agx-faint)',
                fontSize: 12,
                fontWeight: active ? 700 : 500,
                fontFamily: 'inherit',
              }}
            >
              <t.Icon size={13} />
              {t.label}
              {t.badge > 0 && (
                <span
                  style={{
                    padding: '1px 6px',
                    borderRadius: 8,
                    fontSize: 10,
                    fontWeight: 700,
                    background: active ? 'var(--agx-pri2)' : 'var(--agx-border)',
                    color: active ? '#fff' : 'var(--agx-faint)',
                  }}
                >
                  {t.badge}
                </span>
              )}
            </button>
          );
        })}
      </div>

      <div style={{ flex: 1, overflowY: 'auto', padding: 16 }}>
        {tab === 'info' && <InfoTab v={v} data={data} st={st} />}
        {tab === 'pedidos' && <PedidosTab v={v} data={data} st={st} />}
        {tab === 'credito' && <CreditoTab v={v} data={data} />}
      </div>
    </div>
  );
}

function InfoTab({ v, data, st }) {
  const tp = st.ticket_promedio || 0;
  const ant = st.dias_antiguedad;
  const ult = st.dias_ultimo_pedido;
  const credTot = st.total_creditos || 0;
  const credVenc = st.creditos_vencidos || 0;
  const credPagoATiempo = st.creditos_pagados_a_tiempo || 0;
  const codigosTop = (() => {
    try {
      return typeof st.codigos_top === 'string' ? JSON.parse(st.codigos_top) : st.codigos_top || [];
    } catch {
      return (st.codigos_top || '').split(',').map((c) => ({ codigo: c, nombre: c, cantidad: 0 }));
    }
  })();
  const metodosTop = (st.metodos_pago_top || '').split(',').filter(Boolean);
  const tipoPref = st.tipo_entrega_preferido;
  const [prefPopup, setPrefPopup] = useState(false);

  return (
    <div>
      {v('vis.clientes.ubicacion') && <ClientMap data={data} />}

      {/* Resumen financiero */}
      <div style={{ marginBottom: 16 }}>
        <div
          style={{
            fontSize: 11,
            fontWeight: 700,
            color: 'var(--agx-faint)',
            textTransform: 'uppercase',
            marginBottom: 10,
          }}
        >
          Resumen del cliente
        </div>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(100px, 1fr))',
            gap: 8,
          }}
        >
          {[
            {
              l: 'Ticket promedio',
              v: `$${fmtMonto(tp)}`,
              c: 'var(--agx-pri2)',
              bg: 'var(--agx-blue-bg)',
            },
            {
              l: 'Total gastado',
              v: `$${fmtMonto(st.total_completado || st.total_gastado || 0)}`,
              c: 'var(--agx-green)',
              bg: 'var(--agx-green-bg)',
            },
            {
              l: 'Total pedidos',
              v: st.total_pedidos || 0,
              c: 'var(--agx-pri2)',
              bg: 'var(--agx-blue-bg)',
            },
            {
              l: 'Pend. credito',
              v: `$${fmtMonto(st.pendiente_credito || 0)}`,
              c: (st.pendiente_credito || 0) > 0 ? '#ea4335' : 'var(--agx-green)',
              bg: (st.pendiente_credito || 0) > 0 ? '#fef2f2' : 'var(--agx-green-bg)',
            },
            { l: 'Antiguedad', v: ant ? `${ant}d` : '--', c: '#8b5cf6', bg: 'var(--agx-info-bg)' },
            {
              l: 'Ult. pedido',
              v: ult !== null && ult !== undefined ? `Hace ${ult}d` : '--',
              c: '#f59e0b',
              bg: 'var(--agx-amber-bg)',
            },
            { l: 'Mensajes', v: st.total_mensajes || 0, c: '#8b5cf6', bg: 'var(--agx-info-bg)' },
          ].map((s, i) => (
            <div
              key={i}
              style={{
                padding: '10px 12px',
                borderRadius: 12,
                background: s.bg,
                display: 'flex',
                flexDirection: 'column',
                gap: 4,
              }}
            >
              <div
                style={{
                  fontSize: 9,
                  fontWeight: 600,
                  color: s.c,
                  opacity: 0.7,
                  textTransform: 'uppercase',
                }}
              >
                {s.l}
              </div>
              <div style={{ fontSize: 16, fontWeight: 700, color: s.c }}>{s.v}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Estado de pedidos */}
      {v('vis.clientes.estadisticas') && st.total_pedidos > 0 && (
        <div
          style={{
            marginBottom: 16,
            padding: '14px 16px',
            background: 'var(--agx-surface)',
            borderRadius: 14,
            border: '1px solid var(--agx-border)',
          }}
        >
          <div
            style={{
              fontSize: 11,
              fontWeight: 700,
              color: 'var(--agx-faint)',
              textTransform: 'uppercase',
              marginBottom: 10,
            }}
          >
            Estado de pedidos
          </div>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {[
              { l: 'Pendientes', v: st.pedidos_pendientes || 0, c: '#f59e0b' },
              { l: 'En curso', v: st.pedidos_confirmados || 0, c: '#2563eb' },
              { l: 'Entregados', v: st.pedidos_entregados || 0, c: '#34a853' },
              { l: 'Cancelados', v: st.pedidos_cancelados || 0, c: '#ea4335' },
            ].map((p) => (
              <div
                key={p.l}
                style={{
                  flex: '1 1 0',
                  minWidth: 60,
                  textAlign: 'center',
                  padding: '10px 6px',
                  borderRadius: 10,
                  background: 'var(--agx-bg2)',
                }}
              >
                <div style={{ fontSize: 18, fontWeight: 700, color: p.c }}>{p.v}</div>
                <div style={{ fontSize: 9, fontWeight: 500, color: p.c, opacity: 0.8 }}>{p.l}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Credito health */}
      {(credTot > 0 || credVenc > 0) && (
        <div
          style={{
            marginBottom: 16,
            padding: '14px 16px',
            background: 'var(--agx-surface)',
            borderRadius: 14,
            border: '1px solid var(--agx-border)',
          }}
        >
          <div
            style={{
              fontSize: 11,
              fontWeight: 700,
              color: 'var(--agx-faint)',
              textTransform: 'uppercase',
              marginBottom: 10,
            }}
          >
            Salud crediticia
          </div>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            <div
              style={{
                flex: 1,
                minWidth: 80,
                textAlign: 'center',
                padding: '8px',
                borderRadius: 10,
                background: credVenc > 0 ? '#fef2f2' : '#f0fdf4',
              }}
            >
              <div
                style={{
                  fontSize: 20,
                  fontWeight: 700,
                  color: credVenc > 0 ? '#ea4335' : '#34a853',
                }}
              >
                {credVenc}
              </div>
              <div style={{ fontSize: 9, color: credVenc > 0 ? '#ea4335' : '#34a853' }}>
                Vencidos
              </div>
            </div>
            <div
              style={{
                flex: 1,
                minWidth: 80,
                textAlign: 'center',
                padding: '8px',
                borderRadius: 10,
                background: 'var(--agx-bg2)',
              }}
            >
              <div style={{ fontSize: 20, fontWeight: 700, color: 'var(--agx-text)' }}>
                {credTot}
              </div>
              <div style={{ fontSize: 9, color: 'var(--agx-faint)' }}>Total creditos</div>
            </div>
            <div
              style={{
                flex: 1,
                minWidth: 80,
                textAlign: 'center',
                padding: '8px',
                borderRadius: 10,
                background: 'var(--agx-bg2)',
              }}
            >
              <div style={{ fontSize: 20, fontWeight: 700, color: 'var(--agx-green)' }}>
                {credTot > 0 ? Math.round((credPagoATiempo / credTot) * 100) : 0}%
              </div>
              <div style={{ fontSize: 9, color: 'var(--agx-faint)' }}>Pago a tiempo</div>
            </div>
          </div>
          {credTot > 0 && credPagoATiempo < credTot && (
            <div
              style={{
                marginTop: 8,
                fontSize: 10,
                color: '#f59e0b',
                background: '#fff8e1',
                padding: '6px 10px',
                borderRadius: 6,
              }}
            >
              {credPagoATiempo}/{credTot} creditos pagados a tiempo
            </div>
          )}
        </div>
      )}

      {/* Preferencias */}
      {(codigosTop.length > 0 || metodosTop.length > 0 || tipoPref) && (
        <div
          style={{
            marginBottom: 16,
            padding: '14px 16px',
            background: 'var(--agx-surface)',
            borderRadius: 14,
            border: '1px solid var(--agx-border)',
          }}
        >
          <div
            style={{
              fontSize: 11,
              fontWeight: 700,
              color: 'var(--agx-faint)',
              textTransform: 'uppercase',
              marginBottom: 10,
            }}
          >
            Preferencias
          </div>
          {codigosTop.length > 0 && (
            <div
              style={{
                display: 'flex',
                gap: 8,
                alignItems: 'center',
                flexWrap: 'wrap',
                marginBottom: 8,
              }}
            >
              <span style={{ fontSize: 10, color: 'var(--agx-faint)' }}>Productos favoritos:</span>
              <button
                onClick={() => setPrefPopup(true)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6,
                  padding: '6px 14px',
                  borderRadius: 10,
                  border: '1px solid var(--agx-border)',
                  background: 'var(--agx-surface)',
                  cursor: 'pointer',
                  color: 'var(--agx-pri2)',
                  fontSize: 11,
                  fontWeight: 600,
                  fontFamily: 'inherit',
                }}
              >
                <FiPackage size={13} /> Ver {codigosTop.length} producto
                {codigosTop.length !== 1 ? 's' : ''}
              </button>
            </div>
          )}
          {prefPopup && (
            <ProductosTopPopup productos={codigosTop} onClose={() => setPrefPopup(false)} />
          )}
          {metodosTop.length > 0 && (
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 6 }}>
              <span style={{ fontSize: 10, color: 'var(--agx-faint)' }}>Pago:</span>
              {metodosTop.map((m, i) => (
                <span
                  key={m}
                  style={{
                    padding: '2px 8px',
                    borderRadius: 6,
                    fontSize: 10,
                    background: 'var(--agx-bg2)',
                    color: 'var(--agx-muted)',
                    fontWeight: 500,
                  }}
                >
                  {m}
                </span>
              ))}
            </div>
          )}
          {tipoPref && (
            <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
              <span style={{ fontSize: 10, color: 'var(--agx-faint)' }}>Entrega preferida:</span>
              <span
                style={{
                  padding: '2px 8px',
                  borderRadius: 6,
                  fontSize: 10,
                  background: 'var(--agx-green-bg)',
                  color: 'var(--agx-green)',
                  fontWeight: 600,
                }}
              >
                {tipoPref}
              </span>
            </div>
          )}
        </div>
      )}

      {/* Info personal */}
      <div
        style={{
          background: 'var(--agx-surface)',
          borderRadius: 14,
          border: '1px solid var(--agx-border)',
          padding: '14px 16px',
          marginBottom: 16,
        }}
      >
        <div
          style={{
            fontSize: 11,
            fontWeight: 700,
            color: 'var(--agx-faint)',
            textTransform: 'uppercase',
            marginBottom: 12,
          }}
        >
          Informacion personal
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px 16px' }}>
          {[
            v('vis.clientes.cedula') && {
              l: data.tipo === 'juridico' ? 'RIF' : 'Cedula',
              v: data.tipo === 'juridico' ? data.rif : data.cedula,
              mono: true,
            },
            data.tipo === 'juridico' &&
              data.nombre_empresa && { l: 'Empresa', v: data.nombre_empresa },
            v('vis.clientes.acceso') && {
              l: 'Usuario',
              v: data.usuario ? '@' + data.usuario : '--',
              c: 'var(--agx-pri2)',
            },
            { l: 'Tipo', v: data.tipo ? TIPO_LABELS[data.tipo] || data.tipo : '--' },
            data.tipo === 'corporativo' &&
              (data.sedes || []).length > 0 && {
                l: 'Sucursales',
                v: (data.sedes || []).map((s) => s.nombre).filter(Boolean).join(', '),
              },
            data.tipo === 'natural' && { l: 'RIF', v: data.rif, mono: true },
            data.tipo === 'juridico' && data.telefono2 && { l: 'Telefono 2', v: data.telefono2 },
            data.tipo === 'juridico' &&
              data.representante_nombre && { l: 'Representante', v: data.representante_nombre },
            data.tipo === 'juridico' &&
              data.representante_cedula && {
                l: 'CI Rep.',
                v: data.representante_cedula,
                mono: true,
              },
            data.tipo === 'juridico' &&
              data.representante_telefono && { l: 'Tel. Rep.', v: data.representante_telefono },
            v('vis.clientes.acceso') && {
              l: 'Email Verif.',
              v: data.email_verificado ? 'Si' : 'No',
              c: data.email_verificado ? 'var(--agx-green)' : 'var(--agx-faint)',
            },
            {
              l: 'Registro',
              v: data.fecharegistro
                ? new Date(data.fecharegistro).toLocaleDateString('es-VE', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                  })
                : '--',
            },
            { l: 'Atendido por', v: data.atendido_por || 'Bot' },
            {
              l: 'Primer pedido',
              v: st.primer_pedido ? new Date(st.primer_pedido).toLocaleDateString() : '--',
            },
          ]
            .filter(Boolean)
            .map((f, i) => (
              <div key={i}>
                <div
                  style={{
                    fontSize: 9,
                    fontWeight: 600,
                    color: 'var(--agx-faint)',
                    textTransform: 'uppercase',
                    marginBottom: 2,
                  }}
                >
                  {f.l}
                </div>
                <div
                  style={{
                    fontSize: 12,
                    fontWeight: 500,
                    color: f.c || 'var(--agx-muted)',
                    fontFamily: f.mono ? 'monospace' : 'inherit',
                  }}
                >
                  {f.v || '--'}
                </div>
              </div>
            ))}
        </div>
        {v('vis.clientes.direccion') && data.direccion && (
          <div style={{ marginTop: 12, paddingTop: 10, borderTop: '1px solid var(--agx-border)' }}>
            <div
              style={{
                fontSize: 9,
                fontWeight: 600,
                color: 'var(--agx-faint)',
                textTransform: 'uppercase',
                marginBottom: 2,
              }}
            >
              Direccion
            </div>
            <div
              style={{
                fontSize: 12,
                color: 'var(--agx-muted)',
                display: 'flex',
                alignItems: 'center',
                gap: 4,
              }}
            >
              <FiHome size={12} />
              {data.direccion}
            </div>
          </div>
        )}
      </div>

      {/* Direcciones */}
      {v('vis.clientes.direccion') && data.direcciones && data.direcciones.length > 0 && (
        <div
          style={{
            background: 'var(--agx-surface)',
            borderRadius: 14,
            border: '1px solid var(--agx-border)',
            padding: '14px 16px',
            marginBottom: 16,
          }}
        >
          <div
            style={{
              fontSize: 11,
              fontWeight: 700,
              color: 'var(--agx-faint)',
              textTransform: 'uppercase',
              marginBottom: 10,
            }}
          >
            Direcciones ({data.direcciones.length})
          </div>
          {data.direcciones.map((d, i) => (
            <div
              key={d.id}
              style={{
                padding: '8px 0',
                borderTop: i > 0 ? '1px solid var(--agx-border)' : 'none',
                display: 'flex',
                gap: 10,
              }}
            >
              <div
                style={{
                  width: 24,
                  height: 24,
                  borderRadius: 6,
                  background: d.principal ? 'var(--agx-green-bg)' : 'var(--agx-bg2)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}
              >
                <FiMapPin size={12} color={d.principal ? 'var(--agx-green)' : 'var(--agx-faint)'} />
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--agx-muted)' }}>
                    {d.nombre || 'Direccion ' + (i + 1)}
                  </span>
                  {d.principal && (
                    <span
                      style={{
                        padding: '1px 5px',
                        borderRadius: 4,
                        fontSize: 8,
                        fontWeight: 600,
                        background: 'var(--agx-green-bg)',
                        color: 'var(--agx-green)',
                      }}
                    >
                      Principal
                    </span>
                  )}
                </div>
                <div style={{ fontSize: 11, color: 'var(--agx-muted)', marginTop: 2 }}>
                  {d.direccion}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════════
   PEDIDOS TAB
   ══════════════════════════════════════════════════════════════════ */
function PedidosTab({ v, data, st }) {
  const [pedidos, setPedidos] = useState(null);
  const [historialMap, setHistorialMap] = useState({});
  const [expanded, setExpanded] = useState(null);
  const [filtroEstado, setFiltroEstado] = useState('');
  const [busquedaId, setBusquedaId] = useState('');
  const [colapsados, setColapsados] = useState({});

  const ESTADOS_FILTRO = {
    '': 'Todos',
    pendiente: 'Pendientes',
    en_revision: 'En revision',
    aprobado: 'Aprobados',
    esperando_pago: 'Pago pendiente',
    pago_en_verificacion: 'Verificando',
    pagado: 'Pagados',
    armado: 'Armando',
    listo: 'Listo',
    en_camino: 'En camino',
    entregado: 'Entregados',
    credito_pagado: 'Credito pagado',
    rechazado: 'Rechazados',
    cancelado: 'Cancelados',
  };
  const ALL_FASES = [
    'pendiente',
    'en_revision',
    'aprobado',
    'esperando_pago',
    'pago_en_verificacion',
    'pagado',
    'armado',
    'listo',
    'despachado',
    'en_camino',
    'llegada',
    'entregado',
    'credito_pagado',
  ];
  const FASE_COUNT = ALL_FASES.length;

  useEffect(() => {
    API.get(`/clientes/${data.id}/pedidos`)
      .then((r) => {
        if (r.data.status === 'success') setPedidos(r.data.data || []);
      })
      .catch(() => setPedidos([]));
  }, [data.id]);

  const cargarHistorial = async (pid) => {
    if (historialMap[pid]) {
      setExpanded(expanded === pid ? null : pid);
      return;
    }
    try {
      const r = await API.get(`/pedidos/${pid}/historial`);
      if (r.data.status === 'success')
        setHistorialMap((prev) => ({ ...prev, [pid]: r.data.data || [] }));
    } catch {}
    setExpanded(pid);
  };

  const calcDuration = (h, i, hist) => {
    if (i >= hist.length - 1) return null;
    const start = new Date(h.creado_en);
    const end = new Date(hist[i + 1].creado_en);
    const diff = Math.abs(end - start);
    if (diff < 60000) return '<1m';
    if (diff < 3600000) return `${Math.round(diff / 60000)}m`;
    if (diff < 86400000)
      return `${Math.floor(diff / 3600000)}h ${Math.round((diff % 3600000) / 60000)}m`;
    return `${Math.floor(diff / 86400000)}d ${Math.floor((diff % 86400000) / 3600000)}h`;
  };

  const totalDuration = (hist) => {
    if (hist.length < 2) return null;
    const diff = Math.abs(new Date(hist[hist.length - 1].creado_en) - new Date(hist[0].creado_en));
    if (diff < 3600000) return `${Math.round(diff / 60000)}m`;
    if (diff < 86400000) return `${Math.floor(diff / 3600000)}h`;
    return `${Math.floor(diff / 86400000)}d`;
  };

  const prodsImg = (p) => (Array.isArray(p.productos) ? p.productos : []);
  const imgUrl = (url) => getImagenUrl(url);
  const TIPO_ICON = { retiro: FiHome, delivery: FiTruck, encomienda: FiPackage };
  const TIPO_LABEL = { retiro: 'Retiro', delivery: 'Delivery', encomienda: 'Encomienda' };

  // Phase progress (0-1) based on current estado position in ALL_FASES
  const faseIdx = (estado) => {
    const i = ALL_FASES.indexOf(estado);
    return i >= 0 ? i : 0;
  };
  const TERMINALES = ['entregado', 'rechazado', 'cancelado', 'credito_pagado'];
  const isTerminal = (estado) => TERMINALES.includes(estado);

  const filtrados = pedidos
    ? pedidos.filter((p) => {
        if (filtroEstado && p.estado !== filtroEstado) return false;
        if (busquedaId && !String(p.id).includes(busquedaId)) return false;
        return true;
      })
    : [];

  // Agrupar
  const grupos = { activos: [], completados: [], cancelados: [] };
  filtrados.forEach((p) => {
    if (p.estado === 'cancelado' || p.estado === 'rechazado') grupos.cancelados.push(p);
    else if (isTerminal(p.estado)) grupos.completados.push(p);
    else grupos.activos.push(p);
  });
  const toggle = (g) => setColapsados((prev) => ({ ...prev, [g]: !prev[g] }));

  if (!pedidos && st.total_pedidos === 0)
    return (
      <div style={{ textAlign: 'center', padding: 40, color: 'var(--agx-faint)' }}>
        Sin pedidos registrados
      </div>
    );

  return (
    <div>
      <div style={{ display: 'flex', gap: 8, marginBottom: 14, flexWrap: 'wrap' }}>
        <select
          value={filtroEstado}
          onChange={(e) => setFiltroEstado(e.target.value)}
          style={S.filterSel}
        >
          {Object.entries(ESTADOS_FILTRO).map(([k, v]) => (
            <option key={k} value={k}>
              {v}
            </option>
          ))}
        </select>
        <input
          type="text"
          value={busquedaId}
          onChange={(e) => setBusquedaId(e.target.value)}
          placeholder="Buscar por #ID"
          style={{
            padding: '6px 10px',
            borderRadius: 8,
            border: '1px solid var(--agx-border)',
            fontSize: 11,
            background: 'var(--agx-bg2)',
            color: 'var(--agx-text)',
            outline: 'none',
            width: 110,
            fontFamily: 'inherit',
          }}
        />
        {busquedaId && (
          <button onClick={() => setBusquedaId('')} style={S.clr}>
            <FiX size={12} />
          </button>
        )}
        <span
          style={{
            fontSize: 10,
            color: 'var(--agx-faint)',
            marginLeft: 'auto',
            alignSelf: 'center',
          }}
        >
          {filtrados.length} de {pedidos?.length || 0} pedidos
        </span>
      </div>

      {[
        {
          key: 'activos',
          label: 'En curso',
          icon: FiClock,
          cnt: grupos.activos.length,
          color: '#2563eb',
        },
        {
          key: 'completados',
          label: 'Completados',
          icon: FiCheckCircle,
          cnt: grupos.completados.length,
          color: '#34a853',
        },
        {
          key: 'cancelados',
          label: 'Cancelados',
          icon: FiXCircle,
          cnt: grupos.cancelados.length,
          color: '#9e9e9e',
        },
      ].map((g) =>
        g.cnt > 0 ? (
          <div key={g.key} style={{ marginBottom: 12 }}>
            <button
              onClick={() => toggle(g.key)}
              style={{
                width: '100%',
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                padding: '10px 14px',
                borderRadius: 12,
                cursor: 'pointer',
                background: 'var(--agx-surface)',
                border: '1px solid var(--agx-border)',
                fontFamily: 'inherit',
              }}
            >
              <g.icon size={14} color={g.color} />
              <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--agx-text)' }}>
                {g.label}
              </span>
              <span
                style={{
                  padding: '1px 8px',
                  borderRadius: 8,
                  fontSize: 11,
                  fontWeight: 700,
                  background: g.color + '18',
                  color: g.color,
                }}
              >
                {g.cnt}
              </span>
              <div style={{ flex: 1 }} />
              {colapsados[g.key] ? (
                <FiChevronUp size={14} color="var(--agx-faint)" />
              ) : (
                <FiChevronDown size={14} color="var(--agx-faint)" />
              )}
            </button>
            {!colapsados[g.key] &&
              grupos[g.key].map((p) => (
                <PedidoCard
                  key={p.id}
                  p={p}
                  open={expanded === p.id}
                  hist={historialMap[p.id] || []}
                  cargarHistorial={cargarHistorial}
                  calcDuration={calcDuration}
                  totalDuration={totalDuration}
                  prodsImg={prodsImg}
                  imgUrl={imgUrl}
                  faseIdx={faseIdx}
                  FASE_COUNT={FASE_COUNT}
                  isTerminal={isTerminal}
                  TIPO_ICON={TIPO_ICON}
                  TIPO_LABEL={TIPO_LABEL}
                />
              ))}
          </div>
        ) : null
      )}
    </div>
  );
}

function PedidoCard({
  p,
  open,
  hist,
  cargarHistorial,
  calcDuration,
  totalDuration,
  prodsImg,
  imgUrl,
  faseIdx,
  FASE_COUNT,
  isTerminal,
  TIPO_ICON,
  TIPO_LABEL,
}) {
  const etc = PEDIDO_COLOR[p.estado] || '#9e9e9e';
  const total = parseFloat(p.total || 0);
  const isCredito = p.condicion_venta === 'credito';
  const prods = prodsImg(p);
  const diasDesdeCreado = p.fechapedido
    ? Math.round((new Date() - new Date(p.fechapedido)) / 86400000)
    : null;
  const durTotal = totalDuration(hist);
  const prog = Math.min(1, (faseIdx(p.estado) + 1) / FASE_COUNT);
  const completed = isTerminal(p.estado);

  // Estados recorridos para mini timeline
  const estadosRecorridos = [
    ...new Set([
      ...hist.filter((h) => h.estado_nuevo).map((h) => h.estado_nuevo),
      ...(p.estado && !isTerminal(p.estado) ? [p.estado] : []),
      ...(isTerminal(p.estado) && p.estado ? [p.estado] : []),
    ]),
  ];

  // Semantic color for amount
  const amountColor =
    p.estado === 'cancelado' || p.estado === 'rechazado'
      ? '#9e9e9e'
      : completed
        ? '#34a853'
        : p.estado === 'pendiente'
          ? '#f59e0b'
          : 'var(--agx-pri2)';
  const amtBg =
    p.estado === 'cancelado' || p.estado === 'rechazado'
      ? 'transparent'
      : completed
        ? '#f0fdf4'
        : p.estado === 'pendiente'
          ? '#fff8e1'
          : 'var(--agx-blue-bg)';

  const TI = p.tipo_entrega ? TIPO_ICON[p.tipo_entrega] || FiPackage : null;
  const TipoIcon = TI;

  return (
    <div
      style={{
        margin: '4px 0 8px',
        borderRadius: 12,
        background: 'var(--agx-surface)',
        border: '1px solid var(--agx-border)',
        overflow: 'hidden',
      }}
    >
      <div
        onClick={() => cargarHistorial(p.id)}
        style={{ padding: '12px 14px', cursor: 'pointer' }}
      >
        <div style={{ display: 'flex', gap: 10 }}>
          {/* Product thumbnails column */}
          {prods.length > 0 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 2, flexShrink: 0 }}>
              {prods.slice(0, 3).map((pr, i) => (
                <div
                  key={i}
                  style={{
                    width: 34,
                    height: 34,
                    borderRadius: 8,
                    background: 'var(--agx-bg2)',
                    overflow: 'hidden',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  {pr.imagen_url ? (
                    <img
                      src={imgUrl(pr.imagen_url)}
                      alt=""
                      style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                      onError={(e) => {
                        e.target.style.display = 'none';
                      }}
                    />
                  ) : (
                    <FiPackage size={14} color="var(--agx-faint)" />
                  )}
                </div>
              ))}
            </div>
          )}

          <div style={{ flex: 1, minWidth: 0 }}>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                flexWrap: 'wrap',
                marginBottom: 4,
              }}
            >
              <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--agx-text)' }}>
                #{p.id}
              </span>
              <span
                style={{
                  padding: '2px 8px',
                  borderRadius: 6,
                  fontSize: 9,
                  fontWeight: 600,
                  background: etc + '18',
                  color: etc,
                }}
              >
                {ESTADO_PEDIDO[p.estado] || p.estado}
              </span>
              {isCredito && (
                <span
                  style={{
                    padding: '2px 6px',
                    borderRadius: 4,
                    fontSize: 9,
                    fontWeight: 600,
                    background: '#fce8e6',
                    color: '#ea4335',
                  }}
                >
                  Credito
                </span>
              )}
              {TipoIcon && (
                <span
                  title={TIPO_LABEL[p.tipo_entrega]}
                  style={{ display: 'flex', alignItems: 'center' }}
                >
                  <TipoIcon size={11} color="var(--agx-faint)" />
                </span>
              )}
            </div>

            {/* Mini timeline dots */}
            {estadosRecorridos.length > 0 && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 2, marginBottom: 6 }}>
                {estadosRecorridos.map((e, i) => (
                  <React.Fragment key={e}>
                    <div
                      title={ESTADO_PEDIDO[e] || e}
                      style={{
                        width: 6,
                        height: 6,
                        borderRadius: '50%',
                        background: PEDIDO_COLOR[e] || '#9e9e9e',
                        flexShrink: 0,
                      }}
                    />
                    {i < estadosRecorridos.length - 1 && (
                      <div
                        style={{
                          width: 8,
                          height: 1,
                          background: 'var(--agx-border)',
                          flexShrink: 0,
                        }}
                      />
                    )}
                  </React.Fragment>
                ))}
              </div>
            )}

            <div
              style={{
                display: 'flex',
                gap: 10,
                fontSize: 10,
                color: 'var(--agx-muted)',
                flexWrap: 'wrap',
                marginBottom: 4,
              }}
            >
              {p.fechapedido && (
                <span>
                  <FiCalendar size={9} /> {new Date(p.fechapedido).toLocaleDateString()}
                </span>
              )}
              {p.fecha_entrega && (
                <span style={{ color: '#34a853' }}>
                  <FiTruck size={9} /> {new Date(p.fecha_entrega).toLocaleDateString()}
                </span>
              )}
              {diasDesdeCreado !== null && !completed && (
                <span
                  style={{
                    color:
                      diasDesdeCreado > 7
                        ? '#ea4335'
                        : diasDesdeCreado > 3
                          ? '#f59e0b'
                          : 'var(--agx-faint)',
                  }}
                >
                  · {diasDesdeCreado}d en curso
                </span>
              )}
              {completed && durTotal && (
                <span style={{ color: '#34a853' }}>· {durTotal} total</span>
              )}
              {prods.length > 0 && (
                <span style={{ color: 'var(--agx-faint)' }}>
                  {prods.length} prod{prods.length !== 1 ? 's' : ''}
                </span>
              )}
            </div>

            {/* Progress bar */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <div
                style={{
                  flex: 1,
                  height: 3,
                  borderRadius: 2,
                  background: 'var(--agx-border)',
                  overflow: 'hidden',
                }}
              >
                <div
                  style={{
                    height: '100%',
                    borderRadius: 2,
                    background: completed ? '#34a853' : 'var(--agx-pri2)',
                    width: `${Math.round(prog * 100)}%`,
                    transition: 'width 0.3s',
                  }}
                />
              </div>
              <span
                style={{
                  fontSize: 18,
                  fontWeight: 700,
                  whiteSpace: 'nowrap',
                  padding: '2px 10px',
                  borderRadius: 8,
                  background: amtBg,
                  color: amountColor,
                }}
              >
                ${fmtMonto(total)}
              </span>
              {open ? (
                <FiChevronUp size={12} color="var(--agx-faint)" />
              ) : (
                <FiChevronDown size={12} color="var(--agx-faint)" />
              )}
            </div>
          </div>
        </div>
      </div>

      {open && (
        <div style={{ padding: '0 14px 14px', borderTop: '1px solid var(--agx-border)' }}>
          {/* Productos con miniaturas */}
          {prods.length > 0 && (
            <div style={{ marginTop: 10, marginBottom: 8 }}>
              <div
                style={{
                  fontSize: 9,
                  fontWeight: 700,
                  color: 'var(--agx-faint)',
                  textTransform: 'uppercase',
                  marginBottom: 6,
                }}
              >
                Productos
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                {prods.map((pr, i) => (
                  <div
                    key={i}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 6,
                      padding: '4px 10px 4px 4px',
                      borderRadius: 10,
                      background: 'var(--agx-bg2)',
                      border: '1px solid var(--agx-border)',
                    }}
                  >
                    <div
                      style={{
                        width: 28,
                        height: 28,
                        borderRadius: 7,
                        background: 'var(--agx-surface)',
                        overflow: 'hidden',
                        flexShrink: 0,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      {pr.imagen_url ? (
                        <img
                          src={imgUrl(pr.imagen_url)}
                          alt=""
                          style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                          onError={(e) => {
                            e.target.style.display = 'none';
                          }}
                        />
                      ) : (
                        <FiPackage size={12} color="var(--agx-faint)" />
                      )}
                    </div>
                    <div>
                      <div
                        style={{
                          fontSize: 10,
                          fontWeight: 600,
                          color: 'var(--agx-text)',
                          maxWidth: 120,
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}
                      >
                        {pr.nombre}
                      </div>
                      <div style={{ fontSize: 9, color: 'var(--agx-faint)' }}>x{pr.cantidad}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Horizontal timeline compacta */}
          {hist.length > 0 && (
            <div style={{ margin: '8px 0' }}>
              <div
                style={{
                  fontSize: 9,
                  fontWeight: 700,
                  color: 'var(--agx-faint)',
                  textTransform: 'uppercase',
                  marginBottom: 8,
                }}
              >
                Fases ({durTotal || '--'})
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 0, alignItems: 'flex-start' }}>
                {hist.map((h, i) => {
                  const color = PEDIDO_COLOR[h.estado_nuevo] || '#9e9e9e';
                  const dur = calcDuration(h, i, hist);
                  return (
                    <React.Fragment key={h.id}>
                      <div
                        style={{
                          textAlign: 'center',
                          minWidth: 60,
                          flex: '1 0 auto',
                          maxWidth: 110,
                        }}
                      >
                        <div
                          style={{
                            width: 10,
                            height: 10,
                            borderRadius: '50%',
                            background: color,
                            margin: '0 auto 4px',
                          }}
                        />
                        <div style={{ fontSize: 9, fontWeight: 600, color, lineHeight: 1.2 }}>
                          {h.estado_nuevo
                            ? ESTADO_PEDIDO[h.estado_nuevo] || h.estado_nuevo
                            : 'Creado'}
                        </div>
                        <div style={{ fontSize: 8, color: 'var(--agx-faint)' }}>
                          {new Date(h.creado_en).toLocaleTimeString('es-VE', {
                            hour: '2-digit',
                            minute: '2-digit',
                          })}
                        </div>
                        {dur && (
                          <div
                            style={{
                              fontSize: 8,
                              padding: '1px 5px',
                              borderRadius: 4,
                              background: 'var(--agx-bg2)',
                              color: 'var(--agx-faint)',
                              display: 'inline-block',
                              marginTop: 2,
                            }}
                          >
                            {dur}
                          </div>
                        )}
                      </div>
                      {i < hist.length - 1 && (
                        <div
                          style={{
                            flex: '0 0 auto',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            paddingTop: 4,
                            minWidth: 12,
                          }}
                        >
                          <div style={{ width: 12, height: 1, background: 'var(--agx-border)' }} />
                          {hist[i + 1] && (
                            <div
                              style={{
                                fontSize: 7,
                                color: 'var(--agx-faint)',
                                marginTop: 2,
                                whiteSpace: 'nowrap',
                              }}
                            >
                              {hist[i + 1].actor_nombre ||
                                (hist[i + 1].actor_rol === 'cliente'
                                  ? 'Cliente'
                                  : hist[i + 1].actor_rol || 'Sistema')}
                            </div>
                          )}
                        </div>
                      )}
                    </React.Fragment>
                  );
                })}
              </div>
            </div>
          )}

          {/* Credit info */}
          {isCredito && p.fecha_limite_pago && (
            <div
              style={{
                padding: '6px 10px',
                borderRadius: 8,
                background: p.faltante > 0 ? '#fef2f2' : '#f0fdf4',
                fontSize: 10,
                display: 'flex',
                gap: 10,
                flexWrap: 'wrap',
                marginBottom: 6,
              }}
            >
              <span>
                Limite: <strong>{new Date(p.fecha_limite_pago).toLocaleDateString()}</strong>
              </span>
              {(() => {
                const ds = Math.round((new Date() - new Date(p.fecha_limite_pago)) / 86400000);
                return ds > 0 ? (
                  <span style={{ color: '#ea4335', fontWeight: 600 }}>{ds}d retraso</span>
                ) : ds < 0 ? (
                  <span style={{ color: '#34a853' }}>{Math.abs(ds)}d adelanto</span>
                ) : (
                  <span>Vence hoy</span>
                );
              })()}
              {p.faltante > 0 && (
                <span style={{ color: '#ea4335', fontWeight: 600 }}>
                  Falta: ${fmtMonto(p.faltante)}
                </span>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════════
   CREDITO TAB
   ══════════════════════════════════════════════════════════════════ */
function erpMovToCard(m) {
  return {
    origen: 'erp',
    ref: m.referencia,
    total: m.total_usd ?? 0,
    abonado: m.abonado_usd ?? 0,
    faltante: m.saldo_usd ?? 0,
    estado: m.estado,
    fecha_limite_pago: m.vence || null,
    productos: Array.isArray(m.productos) ? m.productos : [],
  };
}

function agoraxCredToCard(c) {
  return {
    ...c,
    origen: 'agorax',
    ref: String(c.id),
    total: c.total ?? 0,
    abonado: c.abonado ?? 0,
    faltante: c.faltante ?? 0,
    fecha_limite_pago: c.fecha_limite_pago || null,
    productos: Array.isArray(c.productos) ? c.productos : [],
  };
}

function CreditoCard({ c, onClick }) {
  const esErp = c.origen === 'erp';
  const total = Number(c.total || 0);
  const abonado = Number(c.abonado || 0);
  const faltante = Number(c.faltante || 0);
  const fechaLimite = c.fecha_limite_pago || null;
  const vencido = faltante > 0 && fechaLimite && new Date(fechaLimite) < new Date();
  const ep = faltante > 0;
  const prods = Array.isArray(c.productos) ? c.productos : [];
  const pct = total > 0 ? Math.min(100, (abonado / total) * 100) : 0;

  return (
    <div
      style={{
        marginBottom: 10,
        borderRadius: 14,
        background: 'var(--agx-surface)',
        border: `1px solid ${ep ? (vencido ? '#fecaca' : '#fde68a') : 'var(--agx-border)'}`,
        overflow: 'hidden',
        transition: 'box-shadow 0.15s',
        cursor: onClick ? 'pointer' : 'default',
      }}
      onClick={onClick}
    >
      <div
        style={{
          padding: '14px 16px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <div style={{ flex: 1, minWidth: 0 }}>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              flexWrap: 'wrap',
              marginBottom: 4,
            }}
          >
            <span style={{ fontSize: 14, fontWeight: 700, color: 'var(--agx-text)' }}>
              {esErp ? `Factura #${c.ref}` : `#${c.ref}`}
            </span>
            {esErp && (
              <span
                style={{
                  padding: '2px 6px',
                  borderRadius: 6,
                  fontSize: 9,
                  fontWeight: 700,
                  letterSpacing: '0.5px',
                  textTransform: 'uppercase',
                  background: 'var(--agx-bg2)',
                  border: '1px solid var(--agx-border)',
                  color: 'var(--agx-muted)',
                }}
              >
                ADVIN
              </span>
            )}
            <span
              style={{
                padding: '3px 10px',
                borderRadius: 8,
                fontSize: 10,
                fontWeight: 600,
                background: ep ? (vencido ? '#fce8e6' : '#fff8e1') : '#e6f4ea',
                color: ep ? (vencido ? '#ea4335' : '#f59e0b') : '#16a34a',
              }}
            >
              {ep ? (vencido ? 'Vencido' : 'Pendiente') : 'Pagado'}
            </span>
          </div>
          <div style={{ fontSize: 11, color: 'var(--agx-muted)', marginBottom: 4 }}>
            Total: ${fmtMonto(total)} · Abonado:{' '}
            <span style={{ color: '#16a34a', fontWeight: 600 }}>${fmtMonto(abonado)}</span>{' '}
            · Falta:{' '}
            <span style={{ color: ep ? '#ea4335' : '#16a34a', fontWeight: 600 }}>
              ${fmtMonto(faltante)}
            </span>
          </div>
          <div
            style={{
              height: 4,
              borderRadius: 2,
              background: 'var(--agx-border)',
              overflow: 'hidden',
            }}
          >
            <div
              style={{
                height: '100%',
                borderRadius: 2,
                background: faltante <= 0 ? '#16a34a' : vencido ? '#ea4335' : '#2563eb',
                width: `${pct}%`,
                transition: 'width 0.4s ease',
              }}
            />
          </div>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              marginTop: 6,
              flexWrap: 'wrap',
            }}
          >
            {prods.slice(0, 3).map((pr, i) => (
              <div
                key={i}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 4,
                  padding: '2px 8px 2px 3px',
                  borderRadius: 8,
                  background: 'var(--agx-bg2)',
                  border: '1px solid var(--agx-border)',
                  fontSize: 10,
                }}
              >
                <div
                  style={{
                    width: 18,
                    height: 18,
                    borderRadius: 5,
                    background: 'var(--agx-surface)',
                    overflow: 'hidden',
                    flexShrink: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  {pr.imagen_url ? (
                    <img
                      src={getImagenUrl(pr.imagen_url)}
                      alt=""
                      style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                      onError={(e) => {
                        e.target.style.display = 'none';
                      }}
                    />
                  ) : (
                    <FiPackage size={9} color="var(--agx-faint)" />
                  )}
                </div>
                <span style={{ fontWeight: 600, color: 'var(--agx-text)' }}>{pr.nombre}</span>
                <span style={{ color: 'var(--agx-muted)' }}>x{pr.cantidad}</span>
              </div>
            ))}
            {prods.length > 3 && (
              <span style={{ fontSize: 10, color: 'var(--agx-faint)' }}>
                +{prods.length - 3} mas
              </span>
            )}
          </div>
          {fechaLimite && (
            <div
              style={{
                fontSize: 9,
                color: vencido ? '#ea4335' : 'var(--agx-faint)',
                marginTop: 4,
                fontWeight: vencido ? 600 : 400,
              }}
            >
              Limite:{' '}
              {new Date(fechaLimite).toLocaleDateString('es-VE', {
                day: '2-digit',
                month: 'short',
                year: 'numeric',
              })}
              {vencido &&
                ` · ${Math.round((new Date() - new Date(fechaLimite)) / 86400000)}d retraso`}
            </div>
          )}
        </div>
        {onClick && (
          <FiChevronRight
            size={16}
            color="var(--agx-faint)"
            style={{ flexShrink: 0, marginLeft: 8 }}
          />
        )}
      </div>
    </div>
  );
}

function ErpCuentaWorker({ cuenta }) {
  const r = cuenta.resumen || {};
  const movs = (cuenta.movimientos || []).filter((m) => m.origen === 'erp');
  const [mostrarTodo, setMostrarTodo] = useState(false);
  const visibles = mostrarTodo ? movs : movs.slice(0, 10);
  return (
    <div style={{ marginBottom: 16 }}>
      <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--agx-faint)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 8 }}>
        Estado de cuenta · ADVIN
      </div>
      <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
        <div style={{ flex: 1, padding: '12px 14px', borderRadius: 14, background: 'var(--agx-surface)', border: '1px solid var(--agx-border)' }}>
          <div style={{ fontSize: 10, color: 'var(--agx-faint)', fontWeight: 600 }}>Saldo pendiente</div>
          <div style={{ fontSize: 20, fontWeight: 700, color: (r.saldo_abierto_usd ?? 0) > 0 ? '#ea4335' : '#16a34a', marginTop: 2 }}>
            ${fmtMonto(r.saldo_abierto_usd ?? 0)}
          </div>
        </div>
        <div style={{ flex: 1, padding: '12px 14px', borderRadius: 14, background: 'var(--agx-surface)', border: '1px solid var(--agx-border)' }}>
          <div style={{ fontSize: 10, color: 'var(--agx-faint)', fontWeight: 600 }}>Limite de credito</div>
          <div style={{ fontSize: 20, fontWeight: 700, color: 'var(--agx-text)', marginTop: 2 }}>
            ${fmtMonto(r.limite_credito_usd ?? 0)}
          </div>
        </div>
        <div style={{ flex: 1, padding: '12px 14px', borderRadius: 14, background: 'var(--agx-surface)', border: '1px solid var(--agx-border)' }}>
          <div style={{ fontSize: 10, color: 'var(--agx-faint)', fontWeight: 600 }}>Disponible</div>
          <div style={{ fontSize: 20, fontWeight: 700, color: '#16a34a', marginTop: 2 }}>
            ${fmtMonto(r.credito_disponible_usd ?? 0)}
          </div>
        </div>
      </div>
      {movs.length > 0 && (
        <div>
          {visibles.map((m, i) => (
            <CreditoCard key={`erp-${m.referencia}-${i}`} c={erpMovToCard(m)} />
          ))}
          {movs.length > 10 && (
            <button onClick={() => setMostrarTodo((s) => !s)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--agx-accent)', fontSize: 11, fontWeight: 600, padding: 4 }}>
              {mostrarTodo ? 'Ver menos' : `Ver todas (${movs.length})`}
            </button>
          )}
        </div>
      )}
    </div>
  );
}

function NotificarCobroBtn({ clienteId }) {
  const [enviando, setEnviando] = useState(false);
  const [ok, setOk] = useState(false);

  const enviar = async () => {
    if (!clienteId) return;
    setEnviando(true);
    setOk(false);
    try {
      const r = await API.post('/credito/notificar-cobro', { cliente_id: clienteId });
      if (r.data.status === 'success') {
        setOk(true);
        setTimeout(() => setOk(false), 3000);
      } else {
        alert(r.data.message || 'Error al enviar la notificación');
      }
    } catch (e) {
      alert(e?.response?.data?.detail || 'Error al enviar la notificación');
    } finally {
      setEnviando(false);
    }
  };

  return (
    <button
      onClick={enviar}
      disabled={enviando}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 6,
        padding: '9px 14px',
        marginBottom: 14,
        borderRadius: 12,
        border: '1px solid #ea4335',
        background: ok ? '#e6f4ea' : '#fff',
        color: ok ? '#16a34a' : '#ea4335',
        fontSize: 12,
        fontWeight: 700,
        cursor: enviando ? 'default' : 'pointer',
        fontFamily: 'inherit',
        opacity: enviando ? 0.6 : 1,
      }}
    >
      <FiSend size={14} />
      {enviando ? 'Enviando…' : ok ? 'Notificación enviada' : 'Notificar cobro'}
    </button>
  );
}

function CreditoTab({ v, data }) {
  const [creditos, setCreditos] = useState(null);
  const [pendientesVerif, setPendientesVerif] = useState([]);
  const [pedidoDetalle, setPedidoDetalle] = useState(null);
  const [historialDetalle, setHistorialDetalle] = useState(null);
  const [visorComprobante, setVisorComprobante] = useState(null);
  const [cuenta, setCuenta] = useState(null);

  useEffect(() => {
    API.get(`/clientes/${data.id}/estado-cuenta`)
      .then((r) => {
        if (r.data.status === 'success') setCuenta(r.data.data || null);
      })
      .catch(() => setCuenta(null));
  }, [data.id]);

  const cargar = useCallback(() => {
    API.get(`/credito/cliente/${data.id}/creditos`)
      .then((r) => {
        if (r.data.status === 'success') setCreditos(r.data.data || []);
      })
      .catch(() => setCreditos([]));
    API.get('/credito/pendientes')
      .then((r) => {
        if (r.data.status === 'success') setPendientesVerif(r.data.data || []);
      })
      .catch(() => {});
  }, [data.id]);

  useEffect(() => {
    cargar();
  }, [cargar]);

  const abrirDetalle = async (c) => {
    setPedidoDetalle(null);
    setHistorialDetalle(null);
    try {
      const [pRes, hRes] = await Promise.all([
        API.get(`/pedidos/${c.id}`, { params: { schema_name: '' } }),
        API.get(`/pedidos/${c.id}/historial`),
      ]);
      if (pRes.data.status === 'success') setPedidoDetalle(pRes.data.data);
      if (hRes.data.status === 'success') setHistorialDetalle(hRes.data.data || []);
    } catch (e) {
      console.error(e);
    }
  };

  const verificarAbono = async (abonoId, aprobado) => {
    try {
      await API.put(`/credito/abono/${abonoId}/verificar`, { aprobado });
      setPendientesVerif((prev) => prev.filter((p) => p.id !== abonoId));
      cargar();
    } catch (e) {
      console.error(e);
    }
  };

  const pend = creditos ? creditos.filter((c) => c.faltante > 0) : [];
  const totPend = pend.reduce((s, c) => s + c.faltante, 0);
  const esErp = !!(cuenta && cuenta.disponible);

  if (pedidoDetalle) {
    return (
      <CreditoDetalle
        pedido={pedidoDetalle}
        historial={historialDetalle}
        onBack={() => setPedidoDetalle(null)}
        cargar={cargar}
      />
    );
  }

  return (
    <div>
      {esErp && <ErpCuentaWorker cuenta={cuenta} />}
      <NotificarCobroBtn clienteId={data.id} clienteNombre={data.nombre} />
      {!esErp && creditos !== null && creditos.length > 0 && (
        <div style={{ display: 'flex', gap: 8, marginBottom: 14 }}>
          <div
            style={{
              flex: 1,
              padding: '12px 14px',
              borderRadius: 14,
              background: 'var(--agx-surface)',
              border: '1px solid var(--agx-border)',
            }}
          >
            <div
              style={{
                fontSize: 10,
                color: 'var(--agx-faint)',
                fontWeight: 600,
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
              }}
            >
              Creditos
            </div>
            <div style={{ fontSize: 22, fontWeight: 700, color: 'var(--agx-text)', marginTop: 2 }}>
              {creditos.length}
            </div>
          </div>
          <div
            style={{
              flex: 1,
              padding: '12px 14px',
              borderRadius: 14,
              background: totPend > 0 ? '#fef2f2' : '#f0fdf4',
              border: `1px solid ${totPend > 0 ? '#fecaca' : '#bbf7d0'}`,
            }}
          >
            <div
              style={{
                fontSize: 10,
                color: totPend > 0 ? '#ea4335' : '#16a34a',
                fontWeight: 600,
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
              }}
            >
              Pendiente
            </div>
            <div
              style={{
                fontSize: 22,
                fontWeight: 700,
                color: totPend > 0 ? '#ea4335' : '#16a34a',
                marginTop: 2,
              }}
            >
              ${fmtMonto(totPend)}
            </div>
          </div>
        </div>
      )}

      {!esErp && creditos === null && pendientesVerif.length === 0 && (
        <div style={{ textAlign: 'center', padding: 40, color: 'var(--agx-faint)', fontSize: 13 }}>
          Sin creditos registrados
        </div>
      )}

      {pendientesVerif.length > 0 && (
        <div style={{ marginBottom: 16 }}>
          <div
            style={{
              fontSize: 11,
              fontWeight: 700,
              color: '#f59e0b',
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
              marginBottom: 10,
            }}
          >
            Abonos por verificar ({pendientesVerif.length})
          </div>
          {pendientesVerif.map((pv) => (
            <div
              key={pv.id}
              style={{
                padding: '14px 16px',
                borderRadius: 14,
                border: '1px solid #fbbf24',
                background: '#fffbeb',
                marginBottom: 8,
              }}
            >
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'flex-start',
                  flexWrap: 'wrap',
                  gap: 8,
                }}
              >
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--agx-text)' }}>
                    Pedido #{pv.pedido_id}
                  </div>
                  <div style={{ fontSize: 12, color: 'var(--agx-muted)', marginTop: 3 }}>
                    ${fmtMonto(pv.monto)} · {pv.metodo_pago || 'Abono'}
                    {pv.referencia && ` · ${pv.referencia}`}
                  </div>
                  <div style={{ fontSize: 10, color: 'var(--agx-faint)', marginTop: 3 }}>
                    {new Date(pv.fecha).toLocaleString('es-VE', {
                      day: '2-digit',
                      month: 'short',
                      hour: '2-digit',
                      minute: '2-digit',
                    })}
                  </div>
                  {pv.comprobante_url && (
                    <button
                      onClick={() => setVisorComprobante(pv.comprobante_url)}
                      style={{
                        padding: '4px 10px',
                        borderRadius: 6,
                        border: '1px solid var(--agx-border)',
                        background: 'var(--agx-surface)',
                        cursor: 'pointer',
                        fontSize: 10,
                        fontWeight: 600,
                        color: 'var(--agx-pri2)',
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 4,
                        marginTop: 6,
                        fontFamily: 'inherit',
                      }}
                    >
                      <FiCamera size={11} /> Ver comprobante
                    </button>
                  )}
                </div>
                <div style={{ display: 'flex', gap: 6 }}>
                  <button
                    onClick={() => verificarAbono(pv.id, true)}
                    style={{
                      padding: '8px 18px',
                      borderRadius: 10,
                      border: 'none',
                      cursor: 'pointer',
                      background: '#16a34a',
                      color: '#fff',
                      fontSize: 12,
                      fontWeight: 600,
                      fontFamily: 'inherit',
                    }}
                  >
                    Confirmar
                  </button>
                  <button
                    onClick={() => verificarAbono(pv.id, false)}
                    style={{
                      padding: '8px 18px',
                      borderRadius: 10,
                      border: 'none',
                      cursor: 'pointer',
                      background: '#ea4335',
                      color: '#fff',
                      fontSize: 12,
                      fontWeight: 600,
                      fontFamily: 'inherit',
                    }}
                  >
                    Rechazar
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {!esErp &&
        creditos &&
        creditos.map((c) => (
          <CreditoCard key={c.id} c={agoraxCredToCard(c)} onClick={() => abrirDetalle(c)} />
        ))}
      {visorComprobante && (
        <VisorComprobante
          comprobanteUrl={visorComprobante}
          onClose={() => setVisorComprobante(null)}
        />
      )}
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════════
   CREDITO DETALLE — Full pedido view from Credito tab
   ══════════════════════════════════════════════════════════════════ */
function CreditoDetalle({ pedido, historial, onBack, cargar }) {
  const prods = Array.isArray(pedido.productos) ? pedido.productos : [];
  const total = parseFloat(pedido.total || 0);
  const faltante = parseFloat(pedido.faltante || 0);
  const isCredito = pedido.condicion_venta === 'credito';
  const vencido =
    faltante > 0 && pedido.fecha_limite_pago && new Date(pedido.fecha_limite_pago) < new Date();
  const [visorComprobante, setVisorComprobante] = useState(null);
  const [abonos, setAbonos] = useState([]);
  const [cargandoAbonos, setCargandoAbonos] = useState(false);
  const [manualForm, setManualForm] = useState(false);
  const [manualMonto, setManualMonto] = useState('');
  const [manualMetodo, setManualMetodo] = useState('');
  const [manualRef, setManualRef] = useState('');
  const [manualSaving, setManualSaving] = useState(false);

  const cargarAbonosL = async () => {
    setCargandoAbonos(true);
    try {
      const r = await API.get(`/credito/pedido/${pedido.id}/abonos`);
      if (r.data.status === 'success') setAbonos(r.data.data || []);
    } catch {}
    setCargandoAbonos(false);
  };

  useEffect(() => {
    if (isCredito) cargarAbonosL();
  }, [pedido.id]); // eslint-disable-line

  const abonarManual = async () => {
    const m = parseFloat(manualMonto);
    if (!m || m <= 0) return;
    setManualSaving(true);
    try {
      await API.post('/credito/abonar-manual', {
        pedido_id: pedido.id,
        monto: m,
        metodo_pago: manualMetodo || 'Efectivo',
        referencia: manualRef || undefined,
      });
      setManualForm(false);
      setManualMonto('');
      setManualMetodo('');
      setManualRef('');
      cargarAbonosL();
      if (cargar) cargar();
    } catch (e) {
      alert(e?.response?.data?.detail || 'Error');
    }
    setManualSaving(false);
  };

  const abonado = total - faltante;
  const pct = total > 0 ? Math.min(100, (abonado / total) * 100) : 0;

  return (
    <div>
      <button
        onClick={onBack}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 6,
          padding: '6px 0',
          border: 'none',
          cursor: 'pointer',
          background: 'transparent',
          color: 'var(--agx-pri2)',
          fontSize: 13,
          fontWeight: 600,
          fontFamily: 'inherit',
          marginBottom: 14,
        }}
      >
        <FiArrowLeft size={14} /> Volver a creditos
      </button>

      <div
        style={{
          padding: 16,
          borderRadius: 16,
          background: 'var(--agx-surface)',
          border: `1px solid ${vencido ? '#fecaca' : 'var(--agx-border)'}`,
          marginBottom: 14,
        }}
      >
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: 12,
          }}
        >
          <div>
            <span style={{ fontSize: 16, fontWeight: 700, color: 'var(--agx-text)' }}>
              Pedido #{pedido.id}
            </span>
            <span
              style={{
                padding: '3px 10px',
                borderRadius: 8,
                fontSize: 10,
                fontWeight: 600,
                background: (PEDIDO_COLOR[pedido.estado] || '#9e9e9e') + '18',
                color: PEDIDO_COLOR[pedido.estado] || '#9e9e9e',
                marginLeft: 8,
              }}
            >
              {ESTADO_PEDIDO[pedido.estado] || pedido.estado}
            </span>
            {isCredito && faltante > 0 && (
              <span
                style={{
                  padding: '3px 10px',
                  borderRadius: 8,
                  fontSize: 10,
                  fontWeight: 600,
                  background: vencido ? '#fce8e6' : '#fff8e1',
                  color: vencido ? '#ea4335' : '#f59e0b',
                  marginLeft: 6,
                }}
              >
                {vencido ? 'Vencido' : 'Pendiente'}
              </span>
            )}
          </div>
        </div>

        <div
          style={{
            display: 'flex',
            gap: 12,
            fontSize: 11,
            color: 'var(--agx-muted)',
            marginBottom: 12,
            flexWrap: 'wrap',
          }}
        >
          <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <FiCalendar size={11} /> Pedido:{' '}
            {pedido.fechapedido
              ? new Date(pedido.fechapedido).toLocaleDateString('es-VE', {
                  day: '2-digit',
                  month: 'short',
                  year: 'numeric',
                })
              : '--'}
          </span>
          {pedido.fecha_entrega && (
            <span style={{ color: '#16a34a', display: 'flex', alignItems: 'center', gap: 4 }}>
              <FiTruck size={11} /> Entrega:{' '}
              {new Date(pedido.fecha_entrega).toLocaleDateString('es-VE', {
                day: '2-digit',
                month: 'short',
                year: 'numeric',
              })}
            </span>
          )}
          {isCredito && pedido.fecha_limite_pago && (
            <span
              style={{
                color: vencido ? '#ea4335' : undefined,
                display: 'flex',
                alignItems: 'center',
                gap: 4,
              }}
            >
              <FiClock size={11} /> Limite:{' '}
              {new Date(pedido.fecha_limite_pago).toLocaleDateString('es-VE', {
                day: '2-digit',
                month: 'short',
                year: 'numeric',
              })}
              {(() => {
                const dias = Math.round(
                  (new Date() - new Date(pedido.fecha_limite_pago)) / 86400000
                );
                return dias > 0 ? (
                  <span style={{ color: '#ea4335', fontWeight: 700, marginLeft: 4 }}>
                    ({dias}d retraso)
                  </span>
                ) : dias < 0 ? (
                  <span style={{ color: '#16a34a', marginLeft: 4 }}>
                    ({Math.abs(dias)}d adelanto)
                  </span>
                ) : (
                  <span style={{ color: '#f59e0b', marginLeft: 4 }}>(Hoy)</span>
                );
              })()}
            </span>
          )}
          {pedido.tipo_entrega && <span>{pedido.tipo_entrega}</span>}
          {pedido.sede_nombre && (
            <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              <FiHome size={11} /> {pedido.sede_nombre}
            </span>
          )}
        </div>

        {isCredito && (
          <>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr 1fr',
                gap: 8,
                marginBottom: 12,
              }}
            >
              <div
                style={{
                  textAlign: 'center',
                  padding: '10px 8px',
                  borderRadius: 10,
                  background: 'var(--agx-bg2)',
                }}
              >
                <div style={{ fontSize: 10, color: 'var(--agx-faint)', fontWeight: 600 }}>
                  Total
                </div>
                <div style={{ fontSize: 18, fontWeight: 700, color: 'var(--agx-text)' }}>
                  ${fmtMonto(total)}
                </div>
              </div>
              <div
                style={{
                  textAlign: 'center',
                  padding: '10px 8px',
                  borderRadius: 10,
                  background: '#f0fdf4',
                }}
              >
                <div style={{ fontSize: 10, color: '#16a34a', fontWeight: 600 }}>Abonado</div>
                <div style={{ fontSize: 18, fontWeight: 700, color: '#16a34a' }}>
                  ${fmtMonto(abonado)}
                </div>
              </div>
              <div
                style={{
                  textAlign: 'center',
                  padding: '10px 8px',
                  borderRadius: 10,
                  background: faltante > 0 ? '#fef2f2' : '#f0fdf4',
                }}
              >
                <div
                  style={{
                    fontSize: 10,
                    color: faltante > 0 ? '#ea4335' : '#16a34a',
                    fontWeight: 600,
                  }}
                >
                  Faltante
                </div>
                <div
                  style={{
                    fontSize: 18,
                    fontWeight: 700,
                    color: faltante > 0 ? '#ea4335' : '#16a34a',
                  }}
                >
                  ${fmtMonto(faltante)}
                </div>
              </div>
            </div>
            <div style={{ marginBottom: 4 }}>
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  fontSize: 10,
                  color: 'var(--agx-faint)',
                  marginBottom: 4,
                }}
              >
                <span>${fmtMonto(abonado)} pagado</span>
                <span>${fmtMonto(total)} total</span>
              </div>
              <div
                style={{
                  height: 6,
                  borderRadius: 3,
                  background: 'var(--agx-border)',
                  overflow: 'hidden',
                }}
              >
                <div
                  style={{
                    height: '100%',
                    borderRadius: 3,
                    background: faltante <= 0 ? '#16a34a' : vencido ? '#ea4335' : '#2563eb',
                    width: `${pct}%`,
                    transition: 'width 0.5s ease',
                  }}
                />
              </div>
            </div>
          </>
        )}
      </div>

      {pedido.comprobante_url && (
        <div
          style={{
            marginBottom: 14,
            borderRadius: 14,
            overflow: 'hidden',
            border: '1px solid var(--agx-border)',
            background: 'var(--agx-surface)',
            padding: 14,
          }}
        >
          <div
            style={{
              fontSize: 11,
              fontWeight: 700,
              color: 'var(--agx-faint)',
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
              marginBottom: 10,
            }}
          >
            Comprobante de pago
          </div>
          <div
            onClick={() => setVisorComprobante(pedido.comprobante_url)}
            style={{
              cursor: 'pointer',
              borderRadius: 10,
              overflow: 'hidden',
              display: 'inline-block',
              background: 'var(--agx-bg2)',
            }}
          >
            <img
              src={getImagenUrl(pedido.comprobante_url)}
              alt="Comprobante"
              style={{ maxWidth: '100%', maxHeight: 280, display: 'block', objectFit: 'contain' }}
            />
            <div
              style={{
                padding: '8px 12px',
                fontSize: 11,
                color: 'var(--agx-pri2)',
                fontWeight: 600,
                textAlign: 'center',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 4,
              }}
            >
              <FiCamera size={12} /> Clic para ampliar
            </div>
          </div>
        </div>
      )}

      {prods.length > 0 && (
        <div
          style={{
            marginBottom: 14,
            borderRadius: 14,
            border: '1px solid var(--agx-border)',
            overflow: 'hidden',
          }}
        >
          <div
            style={{
              padding: '12px 16px',
              background: 'var(--agx-surface)',
              borderBottom: '1px solid var(--agx-border)',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--agx-text)' }}>
              Productos ({prods.length})
            </span>
            <span style={{ fontSize: 10, color: 'var(--agx-muted)' }}>
              Total: {prods.reduce((s, p) => s + p.cantidad, 0)} unidades
            </span>
          </div>
          {prods.map((p, i) => (
            <div
              key={i}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 12,
                padding: '10px 16px',
                background: 'var(--agx-surface)',
                borderBottom: i < prods.length - 1 ? '1px solid var(--agx-border)' : 'none',
              }}
            >
              <div
                style={{
                  width: 44,
                  height: 44,
                  borderRadius: 10,
                  background: 'var(--agx-bg2)',
                  overflow: 'hidden',
                  flexShrink: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                {p.imagen_url ? (
                  <img
                    src={getImagenUrl(p.imagen_url)}
                    alt=""
                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    onError={(e) => {
                      e.target.style.display = 'none';
                    }}
                  />
                ) : (
                  <FiPackage size={18} color="var(--agx-faint)" />
                )}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div
                  style={{
                    fontSize: 13,
                    fontWeight: 600,
                    color: 'var(--agx-text)',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  {p.nombre || p.descripcion || 'Producto'}
                </div>
                <div
                  style={{
                    display: 'flex',
                    gap: 10,
                    flexWrap: 'wrap',
                    marginTop: 3,
                    alignItems: 'center',
                  }}
                >
                  <span style={{ fontSize: 11, color: 'var(--agx-muted)' }}>
                    x{p.cantidad} · ${fmtMonto(p.precio || 0)} c/u
                  </span>
                  <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--agx-pri2)' }}>
                    ${fmtMonto(p.subtotal || 0)}
                  </span>
                  {p.pesable && (
                    <span
                      style={{
                        fontSize: 9,
                        padding: '2px 7px',
                        borderRadius: 5,
                        background: '#dcfce7',
                        color: '#166534',
                        fontWeight: 600,
                      }}
                    >
                      Pesable
                    </span>
                  )}
                </div>
                {p.referencia && (
                  <div style={{ fontSize: 10, color: 'var(--agx-faint)', marginTop: 2 }}>
                    Ref: {p.referencia}
                  </div>
                )}
                {p.combo_id &&
                  Array.isArray(p.componentes) &&
                  p.componentes.length > 0 && (
                    <div style={{ marginTop: 4, paddingLeft: 2 }}>
                      {p.componentes.map((comp, ci) => {
                        const compEtiq = Array.isArray(comp.etiquetas) ? comp.etiquetas : [];
                        return (
                          <div
                            key={ci}
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 5,
                              fontSize: 11,
                              color: 'var(--agx-muted)',
                              padding: '1px 0',
                            }}
                          >
                            <span
                              style={{
                                display: 'inline-block',
                                width: 4,
                                height: 4,
                                borderRadius: '50%',
                                backgroundColor: '#f0b400',
                                flexShrink: 0,
                              }}
                            />
                            <span>{comp.nombre}</span>
                            {compEtiq.length > 0 && (
                              <span style={{ color: 'var(--agx-faint)' }}>
                                ({compEtiq.map((e) => e.nombre).join(', ')})
                              </span>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
              </div>
            </div>
          ))}
        </div>
      )}

      {cargandoAbonos && (
        <div style={{ textAlign: 'center', padding: 20, fontSize: 12, color: 'var(--agx-faint)' }}>
          Cargando abonos...
        </div>
      )}

      {abonos.filter((a) => a.comprobante_url).length > 0 && (
        <div
          style={{
            marginBottom: 14,
            borderRadius: 14,
            border: '1px solid var(--agx-border)',
            overflow: 'hidden',
          }}
        >
          <div
            style={{
              padding: '12px 16px',
              background: 'var(--agx-surface)',
              borderBottom: '1px solid var(--agx-border)',
            }}
          >
            <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--agx-text)' }}>
              Comprobantes de abonos
            </span>
          </div>
          <div
            style={{
              padding: '10px 14px',
              background: 'var(--agx-surface)',
              display: 'flex',
              gap: 8,
              flexWrap: 'wrap',
            }}
          >
            {abonos
              .filter((a) => a.comprobante_url)
              .map((a, i) => (
                <div
                  key={a.id || i}
                  onClick={() => setVisorComprobante(a.comprobante_url)}
                  style={{
                    cursor: 'pointer',
                    borderRadius: 10,
                    overflow: 'hidden',
                    border: '1px solid var(--agx-border)',
                    background: 'var(--agx-bg2)',
                    width: 130,
                  }}
                >
                  <img
                    src={getImagenUrl(a.comprobante_url)}
                    alt="Abono"
                    style={{ width: '100%', height: 100, objectFit: 'cover' }}
                  />
                  <div style={{ padding: '6px 8px', fontSize: 10, color: 'var(--agx-muted)' }}>
                    <div style={{ fontWeight: 600, color: '#16a34a' }}>
                      +${fmtMonto(a.monto)}
                    </div>
                    <div>
                      {new Date(a.fecha).toLocaleDateString('es-VE', {
                        day: '2-digit',
                        month: 'short',
                      })}
                    </div>
                  </div>
                </div>
              ))}
          </div>
        </div>
      )}

      {faltante > 0 && (
        <div
          style={{
            marginBottom: 14,
            borderRadius: 14,
            border: '1px solid var(--agx-border)',
            background: 'var(--agx-surface)',
            padding: 14,
          }}
        >
          {manualForm ? (
            <div>
              <div
                style={{
                  fontSize: 12,
                  fontWeight: 700,
                  color: 'var(--agx-text)',
                  marginBottom: 10,
                }}
              >
                Registrar pago manual
              </div>
              <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
                <input
                  type="number"
                  value={manualMonto}
                  onChange={(e) => setManualMonto(e.target.value)}
                  placeholder="Monto $"
                  min="0.01"
                  max={faltante}
                  step="0.01"
                  style={{
                    flex: 1,
                    padding: '10px 12px',
                    borderRadius: 10,
                    border: '1px solid var(--agx-border)',
                    fontSize: 13,
                    background: 'var(--agx-bg2)',
                    color: 'var(--agx-text)',
                    outline: 'none',
                    fontFamily: 'inherit',
                  }}
                />
                <input
                  type="text"
                  value={manualMetodo}
                  onChange={(e) => setManualMetodo(e.target.value)}
                  placeholder="Metodo"
                  style={{
                    flex: 1,
                    padding: '10px 12px',
                    borderRadius: 10,
                    border: '1px solid var(--agx-border)',
                    fontSize: 13,
                    background: 'var(--agx-bg2)',
                    color: 'var(--agx-text)',
                    outline: 'none',
                    fontFamily: 'inherit',
                  }}
                />
              </div>
              <input
                type="text"
                value={manualRef}
                onChange={(e) => setManualRef(e.target.value)}
                placeholder="Referencia (opcional)"
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  borderRadius: 10,
                  border: '1px solid var(--agx-border)',
                  fontSize: 13,
                  marginBottom: 10,
                  boxSizing: 'border-box',
                  background: 'var(--agx-bg2)',
                  color: 'var(--agx-text)',
                  outline: 'none',
                  fontFamily: 'inherit',
                }}
              />
              <div style={{ display: 'flex', gap: 8 }}>
                <button
                  onClick={() => {
                    setManualForm(false);
                    setManualMonto('');
                    setManualMetodo('');
                    setManualRef('');
                  }}
                  style={S.btnSec}
                >
                  Cancelar
                </button>
                <button
                  onClick={abonarManual}
                  disabled={manualSaving}
                  style={{ ...S.btnPri, opacity: manualSaving ? 0.6 : 1 }}
                >
                  {manualSaving ? 'Guardando...' : 'Registrar pago'}
                </button>
              </div>
            </div>
          ) : (
            <button
              onClick={() => setManualForm(true)}
              style={{
                width: '100%',
                padding: '12px',
                borderRadius: 12,
                border: '1.5px dashed var(--agx-border)',
                background: 'var(--agx-bg2)',
                cursor: 'pointer',
                fontSize: 13,
                fontWeight: 600,
                color: 'var(--agx-pri2)',
                fontFamily: 'inherit',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 6,
              }}
            >
              <FiPlus size={14} /> Registrar pago manual
            </button>
          )}
        </div>
      )}

      {abonos.length > 0 && (
        <div
          style={{
            marginBottom: 14,
            borderRadius: 14,
            border: '1px solid var(--agx-border)',
            overflow: 'hidden',
          }}
        >
          <div
            style={{
              padding: '12px 16px',
              background: 'var(--agx-surface)',
              borderBottom: '1px solid var(--agx-border)',
            }}
          >
            <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--agx-text)' }}>
              Historial de abonos ({abonos.length})
            </span>
          </div>
          {abonos.map((a, i) => (
            <div
              key={a.id}
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: '10px 16px',
                background: 'var(--agx-surface)',
                borderBottom: i < abonos.length - 1 ? '1px solid var(--agx-border)' : 'none',
              }}
            >
              <div style={{ flex: 1 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                  <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--agx-text)' }}>
                    {a.metodo_pago || 'Abono'}
                  </span>
                  {a.estado === 'pendiente' && (
                    <span
                      style={{
                        padding: '2px 7px',
                        borderRadius: 5,
                        fontSize: 10,
                        background: '#fef3c7',
                        color: '#d97706',
                        fontWeight: 600,
                      }}
                    >
                      Pendiente
                    </span>
                  )}
                  {a.estado === 'rechazado' && (
                    <span
                      style={{
                        padding: '2px 7px',
                        borderRadius: 5,
                        fontSize: 10,
                        background: '#fce8e6',
                        color: '#ea4335',
                        fontWeight: 600,
                      }}
                    >
                      Rechazado
                    </span>
                  )}
                </div>
                <div style={{ fontSize: 10, color: 'var(--agx-faint)', marginTop: 3 }}>
                  {new Date(a.fecha).toLocaleString('es-VE', {
                    day: '2-digit',
                    month: 'short',
                    hour: '2-digit',
                    minute: '2-digit',
                  })}{' '}
                  · {a.registrado_nombre || 'Sistema'}
                  {a.referencia && <span> · Ref: {a.referencia}</span>}
                </div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                {a.comprobante_url && (
                  <button
                    onClick={() => setVisorComprobante(a.comprobante_url)}
                    style={{
                      padding: '4px 10px',
                      borderRadius: 6,
                      border: '1px solid var(--agx-border)',
                      background: 'var(--agx-bg2)',
                      cursor: 'pointer',
                      fontSize: 10,
                      fontWeight: 600,
                      color: 'var(--agx-pri2)',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 4,
                      fontFamily: 'inherit',
                    }}
                  >
                    <FiCamera size={10} /> Ver
                  </button>
                )}
                <span
                  style={{
                    fontSize: 15,
                    fontWeight: 700,
                    color: a.estado === 'rechazado' ? '#ea4335' : '#16a34a',
                  }}
                >
                  +${fmtMonto(a.monto)}
                </span>
              </div>
            </div>
          ))}
        </div>
      )}

      {historial && historial.length > 0 && (
        <div
          style={{
            marginBottom: 14,
            borderRadius: 14,
            border: '1px solid var(--agx-border)',
            overflow: 'hidden',
          }}
        >
          <div
            style={{
              padding: '12px 16px',
              background: 'var(--agx-surface)',
              borderBottom: '1px solid var(--agx-border)',
            }}
          >
            <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--agx-text)' }}>
              Historial de fases
            </span>
          </div>
          <div style={{ padding: '10px 16px', background: 'var(--agx-surface)' }}>
            {historial.map((h, i) => {
              const color = PEDIDO_COLOR[h.estado_nuevo] || '#9e9e9e';
              return (
                <div key={h.id} style={{ display: 'flex', gap: 12 }}>
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      paddingTop: 3,
                    }}
                  >
                    <div
                      style={{
                        width: 10,
                        height: 10,
                        borderRadius: '50%',
                        background: color,
                        border: '2px solid ' + color + '30',
                      }}
                    />
                    {i < historial.length - 1 && (
                      <div
                        style={{ width: 2, flex: 1, background: 'var(--agx-border)', minHeight: 8 }}
                      />
                    )}
                  </div>
                  <div style={{ flex: 1, paddingBottom: 12 }}>
                    <div style={{ fontSize: 12, fontWeight: 600, color }}>
                      {h.estado_nuevo ? ESTADO_PEDIDO[h.estado_nuevo] || h.estado_nuevo : 'Creado'}
                    </div>
                    <div
                      style={{
                        fontSize: 10,
                        color: 'var(--agx-faint)',
                        marginTop: 2,
                        lineHeight: 1.4,
                      }}
                    >
                      {new Date(h.creado_en).toLocaleString('es-VE', {
                        day: '2-digit',
                        month: 'short',
                        hour: '2-digit',
                        minute: '2-digit',
                      })}
                      {h.actor_nombre && (
                        <span style={{ fontWeight: 500, color: 'var(--agx-muted)', marginLeft: 4 }}>
                          · {h.actor_nombre}
                        </span>
                      )}
                      {h.actor_rol && (
                        <span style={{ color: 'var(--agx-faint)' }}> ({h.actor_rol})</span>
                      )}
                      {h.nota && <span style={{ marginLeft: 4 }}>· {h.nota}</span>}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {visorComprobante && (
        <VisorComprobante
          comprobanteUrl={visorComprobante}
          onClose={() => setVisorComprobante(null)}
        />
      )}
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════════
   SUB-COMPONENTS
   ══════════════════════════════════════════════════════════════════ */
function LocationPicker({ lat, lng, direccion, onLocationChange }) {
  const [searchText, setSearchText] = useState(direccion || '');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [searchError, setSearchError] = useState('');
  const [showResults, setShowResults] = useState(false);
  const pos = lat && lng ? [parseFloat(lat), parseFloat(lng)] : [10.4806, -66.9036];

  const doSearch = useCallback(async (query) => {
    if (!query || query.length < 3) return;
    setSearching(true);
    setSearchError('');
    try {
      const res = await API.get(`/geocodificar/buscar?q=${encodeURIComponent(query)}&limit=5`);
      if (res.data.status === 'success') {
        setSearchResults(res.data.data);
        setShowResults(true);
      } else {
        setSearchError(res.data.message || 'No se encontro la direccion');
      }
    } catch {
      setSearchError('No se encontro la direccion');
      setSearchResults([]);
    }
    setSearching(false);
  }, []);

  const selectResult = (result) => {
    const newLat = parseFloat(result.lat);
    const newLng = parseFloat(result.lon);
    setSearchText(result.display_name);
    setShowResults(false);
    setSearchResults([]);
    if (onLocationChange) onLocationChange(newLat, newLng, result.display_name);
  };

  const reverseGeocode = useCallback(
    async (newLat, newLng) => {
      let addr = '';
      try {
        const res = await API.get(`/geocodificar/reverse?lat=${newLat}&lng=${newLng}`);
        if (res.data.status === 'success' && res.data.data) {
          addr = res.data.data.display_name || '';
          setSearchText(addr);
        }
      } catch {}
      if (onLocationChange) onLocationChange(newLat, newLng, addr);
    },
    [onLocationChange]
  );

  const handleMapClick = useCallback(
    (e) => {
      reverseGeocode(e.latlng.lat, e.latlng.lng);
    },
    [reverseGeocode]
  );

  const handleMarkerDrag = useCallback(
    (e) => {
      const { lat: newLat, lng: newLng } = e.target.getLatLng();
      reverseGeocode(newLat, newLng);
    },
    [reverseGeocode]
  );

  return (
    <div style={{ marginBottom: 12 }}>
      <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--agx-muted)', marginBottom: 4 }}>
        Ubicacion GPS
      </div>
      <div style={{ display: 'flex', gap: 6, marginBottom: 8 }}>
        <div style={{ flex: 1, position: 'relative' }}>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              padding: '0 12px',
              borderRadius: 10,
              border: '1px solid var(--agx-border)',
              background: 'var(--agx-surface)',
            }}
          >
            <FiMapPin size={13} color="var(--agx-faint)" />
            <input
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && doSearch(searchText)}
              placeholder="Buscar direccion en el mapa..."
              style={{
                flex: 1,
                border: 'none',
                background: 'transparent',
                padding: '10px 0',
                fontSize: 13,
                outline: 'none',
                color: 'var(--agx-text)',
                fontFamily: 'inherit',
              }}
            />
            {searchText && (
              <button
                onClick={() => {
                  setSearchText('');
                  setSearchResults([]);
                  setShowResults(false);
                }}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  color: 'var(--agx-faint)',
                  padding: 2,
                }}
              >
                <FiX size={12} />
              </button>
            )}
          </div>
          <button
            onClick={() => doSearch(searchText)}
            disabled={searching || !searchText || searchText.length < 3}
            style={{
              position: 'absolute',
              right: 4,
              top: 4,
              bottom: 4,
              padding: '0 12px',
              borderRadius: 8,
              border: 'none',
              cursor: 'pointer',
              background: 'var(--agx-pri2)',
              color: '#fff',
              fontSize: 11,
              fontWeight: 600,
              fontFamily: 'inherit',
              opacity: searching || !searchText || searchText.length < 3 ? 0.5 : 1,
            }}
          >
            {searching ? '...' : 'Buscar'}
          </button>
          {showResults && searchResults.length > 0 && (
            <div
              style={{
                position: 'absolute',
                top: '100%',
                left: 0,
                right: 0,
                background: 'var(--agx-surface)',
                border: '1px solid var(--agx-border)',
                borderRadius: 10,
                marginTop: 4,
                zIndex: 2100,
                maxHeight: 200,
                overflowY: 'auto',
                boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
              }}
            >
              {searchResults.map((r, i) => (
                <div
                  key={i}
                  onClick={() => selectResult(r)}
                  style={{
                    padding: '8px 12px',
                    cursor: 'pointer',
                    fontSize: 11,
                    color: 'var(--agx-text)',
                    borderBottom:
                      i < searchResults.length - 1 ? '1px solid var(--agx-border)' : 'none',
                  }}
                >
                  {r.display_name}
                </div>
              ))}
            </div>
          )}
          {searchError && (
            <div style={{ fontSize: 10, color: '#ea4335', marginTop: 4 }}>{searchError}</div>
          )}
        </div>
      </div>
      <div style={{ borderRadius: 12, overflow: 'hidden', border: '1px solid var(--agx-border)' }}>
        <MapContainer
          center={pos}
          zoom={13}
          style={{ height: 200, width: '100%' }}
          zoomControl={false}
        >
          <TileLayer attribution="" url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
          <MapClickHandler onClick={handleMapClick} />
          {lat && lng && (
            <DraggableMarker
              lat={parseFloat(lat)}
              lng={parseFloat(lng)}
              onDragEnd={handleMarkerDrag}
            />
          )}
        </MapContainer>
      </div>
      {lat && lng && (
        <div
          style={{ display: 'flex', gap: 8, marginTop: 6, fontSize: 10, color: 'var(--agx-faint)' }}
        >
          <span>Lat: {parseFloat(lat).toFixed(6)}</span>
          <span>Lng: {parseFloat(lng).toFixed(6)}</span>
        </div>
      )}
    </div>
  );
}

function MapClickHandler({ onClick }) {
  useMapEvents({ click: onClick });
  return null;
}

function DraggableMarker({ lat, lng, onDragEnd }) {
  const markerRef = useRef(null);
  const eventHandlers = useMemo(() => ({ dragend: onDragEnd }), [onDragEnd]);
  return (
    <Marker draggable={true} position={[lat, lng]} ref={markerRef} eventHandlers={eventHandlers}>
      <Popup>Arrastra para ajustar la ubicacion</Popup>
    </Marker>
  );
}

function ClientMap({ data }) {
  const pos = data.lat && data.lng ? [parseFloat(data.lat), parseFloat(data.lng)] : null;
  if (!pos) return null;
  const dirs = (data.direcciones || [])
    .filter((d) => d.lat && d.lng)
    .map((d) => [parseFloat(d.lat), parseFloat(d.lng)]);
  return (
    <div
      style={{
        marginBottom: 16,
        borderRadius: 14,
        overflow: 'hidden',
        border: '1px solid var(--agx-border)',
      }}
    >
      <MapContainer
        center={pos}
        zoom={13}
        style={{ height: 180, width: '100%' }}
        zoomControl={false}
        scrollWheelZoom={false}
      >
        <TileLayer attribution="" url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
        <Marker position={pos}>
          <Popup>{[data.nombre, data.apellido].filter(Boolean).join(' ')}</Popup>
        </Marker>
        {dirs.map((d, i) => (
          <Marker key={i} position={d}>
            <Popup>Direccion {i + 1}</Popup>
          </Marker>
        ))}
      </MapContainer>
    </div>
  );
}

function FormModal({ form, setForm, editMode, onSave, saving, onClose }) {
  const set = (key) => (e) => setForm((prev) => ({ ...prev, [key]: e.target.value }));
  const [rifError, setRifError] = useState('');
  const [cedulaError, setCedulaError] = useState('');
  const [cedulaSugerencias, setCedulaSugerencias] = useState([]);
  const cedulaTimerRef = useRef(null);

  const setSucursal = (idx, field, value) => {
    setForm((prev) => {
      const sedes = [...(prev.sedes || [])];
      sedes[idx] = { ...(sedes[idx] || {}), [field]: value };
      return { ...prev, sedes };
    });
  };
  const addSucursal = () => {
    setForm((prev) => ({
      ...prev,
      sedes: [...(prev.sedes || []), { nombre: '', direccion: '', telefono: '', email: '', representante: '', representante_cedula: '', representante_telefono: '', rif: '' }],
    }));
  };
  const removeSucursal = (idx) => {
    setForm((prev) => ({ ...prev, sedes: (prev.sedes || []).filter((_, i) => i !== idx) }));
  };

  const handleRifChange = (e) => {
    const val = e.target.value.toUpperCase();
    setForm((prev) => ({ ...prev, rif: val }));
    if (val && !RIF_REGEX.test(val)) {
      setRifError('Formato: J-12345678-9');
    } else {
      setRifError('');
    }
  };

  const handleCedulaChange = (e) => {
    const val = e.target.value;
    setForm((prev) => ({ ...prev, cedula: val }));
    if (val.trim() && !esCedulaValida(val)) {
      setCedulaError('La cédula debe tener entre 6 y 10 dígitos');
    } else {
      setCedulaError('');
    }
    const digitos = val.replace(/[^0-9]/g, '');
    if (cedulaTimerRef.current) clearTimeout(cedulaTimerRef.current);
    if (!editMode && digitos.length >= 5) {
      cedulaTimerRef.current = setTimeout(() => {
        API.get('/clientes/sugerir-cedula', { params: { q: val.trim() } })
          .then((r) => setCedulaSugerencias(r.data?.data || []))
          .catch(() => setCedulaSugerencias([]));
      }, 300);
    } else {
      setCedulaSugerencias([]);
    }
  };

  const autofillCliente = (c) => {
    setForm((prev) => ({
      ...prev,
      cedula: c.cedula || prev.cedula,
      nombre: c.nombre || prev.nombre,
      apellido: c.apellido || prev.apellido,
      telefono: c.telefono || prev.telefono,
      email: c.email || prev.email,
      direccion: c.direccion || prev.direccion,
      rif: c.rif || prev.rif,
      nombre_empresa: c.nombre_empresa || prev.nombre_empresa,
    }));
    setCedulaSugerencias([]);
    setCedulaError('');
  };

  const handleLocationChange = (newLat, newLng, addr) => {
    setForm((prev) => ({
      ...prev,
      lat: String(newLat),
      lng: String(newLng),
      direccion: addr || prev.direccion,
    }));
  };

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.4)',
        zIndex: 2000,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 16,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: '100%',
          maxWidth: 520,
          maxHeight: '90vh',
          background: 'var(--agx-surface)',
          borderRadius: 20,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            padding: '16px 20px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <div>
            <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--agx-text)' }}>
              {editMode ? 'Editar cliente' : 'Nuevo cliente'}
            </div>
            <div style={{ fontSize: 11, color: 'var(--agx-faint)' }}>
              {form.tipo === 'natural'
                ? 'Persona natural'
                : form.tipo === 'corporativo'
                  ? 'Cliente corporativo multi-sede'
                  : 'Empresa / Persona juridica'}
            </div>
          </div>
          <button
            onClick={onClose}
            style={{
              width: 28,
              height: 28,
              borderRadius: '50%',
              border: 'none',
              cursor: 'pointer',
              background: 'var(--agx-bg2)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'var(--agx-faint)',
            }}
          >
            <FiX size={14} />
          </button>
        </div>
        <div style={{ flex: 1, overflowY: 'auto', padding: '4px 20px 16px' }}>
          <Section title="Tipo de cliente">
            <div style={{ display: 'flex', gap: 6, marginBottom: 4 }}>
              {['natural', 'juridico', 'corporativo'].map((t) => {
                const sel = form.tipo === t;
                return (
                  <button
                    key={t}
                    onClick={() => setForm((prev) => ({ ...prev, tipo: t }))}
                    style={{
                      flex: 1,
                      padding: '8px',
                      borderRadius: 10,
                      border: sel ? '2px solid var(--agx-pri2)' : '1px solid var(--agx-border)',
                      background: sel ? 'var(--agx-blue-bg)' : 'var(--agx-surface)',
                      cursor: 'pointer',
                      fontSize: 12,
                      fontWeight: 600,
                      color: sel ? 'var(--agx-pri2)' : 'var(--agx-muted)',
                      fontFamily: 'inherit',
                    }}
                  >
                    {TIPO_LABELS[t]}
                  </button>
                );
              })}
            </div>
          </Section>

          {form.tipo === 'corporativo' && (
            <Section title="Sucursales del cliente">
              <div style={{ fontSize: 11, color: 'var(--agx-faint)', marginBottom: 8 }}>
                Agrega las sucursales de este cliente. Cada una generara un pedido con su propia direccion de entrega.
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {(form.sedes || []).length === 0 && (
                  <div style={{ fontSize: 12, color: 'var(--agx-muted)' }}>Sin sucursales. Agrega al menos una.</div>
                )}
                {(form.sedes || []).map((s, idx) => (
                  <div
                    key={idx}
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 6,
                      padding: 10,
                      borderRadius: 12,
                      border: '1px solid var(--agx-border)',
                      background: 'var(--agx-surface)',
                    }}
                  >
                    <div style={{ display: 'flex', gap: 6 }}>
                      <Input
                        label="Nombre de la sucursal"
                        value={s.nombre || ''}
                        onChange={(e) => setSucursal(idx, 'nombre', e.target.value)}
                        placeholder="Sucursal Centro"
                      />
                      <button
                        onClick={() => removeSucursal(idx)}
                        style={{
                          flexShrink: 0,
                          marginTop: 22,
                          width: 28,
                          height: 28,
                          borderRadius: 8,
                          border: '1px solid var(--agx-border)',
                          background: 'var(--agx-bg2)',
                          color: 'var(--agx-faint)',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        <FiX size={13} />
                      </button>
                    </div>
                    <Input
                      label="Direccion de entrega"
                      value={s.direccion || ''}
                      onChange={(e) => setSucursal(idx, 'direccion', e.target.value)}
                      placeholder="Av. Principal, local 2"
                      icon={<FiMapPin size={13} />}
                    />
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                      <Input
                        label="RIF de la sucursal"
                        value={s.rif || ''}
                        onChange={(e) => setSucursal(idx, 'rif', e.target.value)}
                        placeholder="J-12345678-9"
                      />
                      <Input
                        label="Telefono"
                        value={s.telefono || ''}
                        onChange={(e) => setSucursal(idx, 'telefono', e.target.value)}
                        placeholder="0412-1234567"
                        icon={<FiPhone size={13} />}
                      />
                      <Input
                        label="Correo (email)"
                        value={s.email || ''}
                        onChange={(e) => setSucursal(idx, 'email', e.target.value)}
                        placeholder="sucursal@empresa.com"
                        icon={<FiMail size={13} />}
                      />
                      <Input
                        label="Cedula del encargado"
                        value={s.representante_cedula || ''}
                        onChange={(e) => setSucursal(idx, 'representante_cedula', e.target.value)}
                        placeholder="V-12345678"
                      />
                    </div>
                    <Input
                      label="Representante"
                      value={s.representante || ''}
                      onChange={(e) => setSucursal(idx, 'representante', e.target.value)}
                      placeholder="Nombre del encargado"
                      icon={<FiUser size={13} />}
                    />
                    <Input
                      label="Telefono del encargado"
                      value={s.representante_telefono || ''}
                      onChange={(e) => setSucursal(idx, 'representante_telefono', e.target.value)}
                      placeholder="0412-7654321"
                      icon={<FiPhone size={13} />}
                    />
                  </div>
                ))}
                <button
                  onClick={addSucursal}
                  style={{
                    padding: '8px',
                    borderRadius: 10,
                    border: '1px dashed var(--agx-border)',
                    background: 'transparent',
                    cursor: 'pointer',
                    fontSize: 12,
                    fontWeight: 600,
                    color: 'var(--agx-pri2)',
                    fontFamily: 'inherit',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 6,
                  }}
                >
                  <FiPlus size={13} /> Agregar sucursal
                </button>
              </div>
            </Section>
          )}

          <Section title="Credenciales">
            <Input
              label="Usuario"
              value={form.usuario}
              onChange={set('usuario')}
              icon={<FiUser size={13} />}
            />
            <div style={{ display: 'flex', gap: 8 }}>
              <Input
                label="Contrasena"
                value={form.clave}
                onChange={set('clave')}
                type="password"
                icon={<FiLock size={13} />}
              />
              <button
                onClick={() => setForm((prev) => ({ ...prev, clave: generatePassword() }))}
                style={{
                  padding: '6px 10px',
                  borderRadius: 8,
                  border: 'none',
                  cursor: 'pointer',
                  background: 'var(--agx-blue-bg)',
                  color: 'var(--agx-pri2)',
                  fontSize: 11,
                  fontWeight: 600,
                  flexShrink: 0,
                  marginTop: 24,
                }}
              >
                <FiKey size={12} /> Gen
              </button>
            </div>
          </Section>

          {form.tipo === 'natural' ? (
            <Section title="Datos personales">
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                <div style={{ position: 'relative' }}>
                  <Input
                    label="CI"
                    value={form.cedula}
                    onChange={handleCedulaChange}
                    icon={<FiUser size={13} />}
                  />
                  {cedulaError && (
                    <div style={{ fontSize: 10, color: '#ea4335', marginTop: -8, marginBottom: 8 }}>
                      {cedulaError}
                    </div>
                  )}
                  {cedulaSugerencias.length > 0 && (
                    <div
                      style={{
                        position: 'absolute',
                        left: 0,
                        right: 0,
                        top: '100%',
                        zIndex: 30,
                        marginTop: -12,
                        background: 'var(--agx-surface)',
                        border: '1px solid var(--agx-border)',
                        borderRadius: 10,
                        boxShadow: '0 8px 24px rgba(0,0,0,0.14)',
                        overflow: 'hidden',
                      }}
                    >
                      {cedulaSugerencias.map((c) => (
                        <button
                          key={c.id}
                          type="button"
                          onClick={() => autofillCliente(c)}
                          style={{
                            display: 'block',
                            width: '100%',
                            textAlign: 'left',
                            padding: '8px 12px',
                            border: 'none',
                            background: 'transparent',
                            cursor: 'pointer',
                            fontSize: 12,
                            color: 'var(--agx-text)',
                            borderBottom: '1px solid var(--agx-border)',
                          }}
                          onMouseEnter={(ev) => (ev.currentTarget.style.background = 'var(--agx-bg2)')}
                          onMouseLeave={(ev) => (ev.currentTarget.style.background = 'transparent')}
                        >
                          <span style={{ fontWeight: 700 }}>
                            {c.nombre || 'Sin nombre'}
                            {c.apellido ? ` ${c.apellido}` : ''}
                          </span>
                          <span style={{ color: 'var(--agx-muted)', marginLeft: 6 }}>
                            · {c.cedula}
                          </span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                <Input
                  label="Telefono"
                  value={form.telefono}
                  onChange={set('telefono')}
                  icon={<FiPhone size={13} />}
                />
              </div>
              <Input
                label="Nombre"
                value={form.nombre}
                onChange={set('nombre')}
                icon={<FiUser size={13} />}
              />
              <Input
                label="Apellido"
                value={form.apellido}
                onChange={set('apellido')}
                icon={<FiUser size={13} />}
              />
              <Input
                label="Email"
                value={form.email}
                onChange={set('email')}
                icon={<FiMail size={13} />}
              />
              <LocationPicker
                lat={form.lat}
                lng={form.lng}
                direccion={form.direccion}
                onLocationChange={handleLocationChange}
              />
            </Section>
          ) : (
            <>
              <Section title="Datos de la empresa">
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                  <div>
                    <Input
                      label="RIF"
                      value={form.rif}
                      onChange={handleRifChange}
                      icon={<FiBriefcase size={13} />}
                    />
                    {rifError && (
                      <div
                        style={{ fontSize: 10, color: '#ea4335', marginTop: -8, marginBottom: 8 }}
                      >
                        {rifError}
                      </div>
                    )}
                  </div>
                  <Input
                    label="Nombre de la empresa"
                    value={form.nombre_empresa}
                    onChange={set('nombre_empresa')}
                    icon={<FiBriefcase size={13} />}
                  />
                </div>
                <LocationPicker
                  lat={form.lat}
                  lng={form.lng}
                  direccion={form.direccion}
                  onLocationChange={handleLocationChange}
                />
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                  <Input
                    label="Telefono 1"
                    value={form.telefono}
                    onChange={set('telefono')}
                    icon={<FiPhone size={13} />}
                  />
                  <Input
                    label="Telefono 2"
                    value={form.telefono2}
                    onChange={set('telefono2')}
                    icon={<FiPhone size={13} />}
                  />
                </div>
                <Input
                  label="Email"
                  value={form.email}
                  onChange={set('email')}
                  icon={<FiMail size={13} />}
                />
              </Section>

              <Section title="Datos del representante">
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                  <Input
                    label="Cedula"
                    value={form.representante_cedula}
                    onChange={set('representante_cedula')}
                    icon={<FiUser size={13} />}
                  />
                  <Input
                    label="Telefono"
                    value={form.representante_telefono}
                    onChange={set('representante_telefono')}
                    icon={<FiPhone size={13} />}
                  />
                </div>
                <Input
                  label="Nombre"
                  value={form.representante_nombre}
                  onChange={set('representante_nombre')}
                  icon={<FiUser size={13} />}
                />
              </Section>
            </>
          )}
        </div>
        <div
          style={{
            padding: '12px 20px',
            borderTop: '1px solid var(--agx-border)',
            display: 'flex',
            gap: 8,
          }}
        >
          <button onClick={onClose} style={S.btnSec}>
            Cancelar
          </button>
          <button
            onClick={onSave}
            disabled={saving}
            style={{ ...S.btnPri, opacity: saving ? 0.6 : 1 }}
          >
            {saving ? 'Guardando...' : 'Guardar'}
          </button>
        </div>
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div
      style={{
        marginBottom: 16,
        padding: 14,
        background: 'var(--agx-bg2)',
        borderRadius: 12,
        border: '1px solid var(--agx-border)',
      }}
    >
      <div
        style={{
          fontSize: 11,
          fontWeight: 700,
          color: 'var(--agx-muted)',
          marginBottom: 10,
          display: 'flex',
          alignItems: 'center',
          gap: 6,
        }}
      >
        {title}
        <div style={{ flex: 1, height: 1, background: 'var(--agx-border)' }} />
      </div>
      {children}
    </div>
  );
}

function Input({ label, value, onChange, icon, type, disabled, placeholder }) {
  return (
    <div style={{ marginBottom: 12 }}>
      {label && (
        <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--agx-muted)', marginBottom: 4 }}>
          {label}
        </div>
      )}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          padding: '0 12px',
          borderRadius: 10,
          border: '1px solid var(--agx-border)',
          background: 'var(--agx-surface)',
          opacity: disabled ? 0.5 : 1,
        }}
      >
        {icon && <span style={{ color: 'var(--agx-faint)', flexShrink: 0 }}>{icon}</span>}
        <input
          value={value || ''}
          onChange={onChange}
          type={type || 'text'}
          disabled={disabled}
          placeholder={placeholder}
          style={{
            flex: 1,
            border: 'none',
            background: 'transparent',
            padding: '10px 0',
            fontSize: 13,
            outline: 'none',
            color: 'var(--agx-text)',
            fontFamily: 'inherit',
          }}
        />
      </div>
    </div>
  );
}

function ProductosTopPopup({ productos, onClose }) {
  const [orden, setOrden] = useState('desc');
  const sorted = [...productos].sort((a, b) =>
    orden === 'desc' ? (b.cantidad || 0) - (a.cantidad || 0) : (a.cantidad || 0) - (b.cantidad || 0)
  );

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.5)',
        zIndex: 3000,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 20,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: '100%',
          maxWidth: 400,
          maxHeight: '70vh',
          overflowY: 'auto',
          padding: 20,
          borderRadius: 20,
          background: 'var(--agx-surface)',
          boxShadow: '0 20px 60px rgba(0,0,0,0.2)',
        }}
      >
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: 16,
          }}
        >
          <span style={{ fontSize: 15, fontWeight: 700, color: 'var(--agx-text)' }}>
            Productos favoritos
          </span>
          <button
            onClick={onClose}
            style={{
              width: 28,
              height: 28,
              borderRadius: '50%',
              border: 'none',
              cursor: 'pointer',
              background: 'var(--agx-bg2)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'var(--agx-faint)',
            }}
          >
            <FiX size={14} />
          </button>
        </div>
        <div style={{ display: 'flex', gap: 6, marginBottom: 12 }}>
          <button
            onClick={() => setOrden('desc')}
            style={{
              padding: '5px 12px',
              borderRadius: 8,
              border: '1px solid var(--agx-border)',
              background: orden === 'desc' ? 'var(--agx-blue-bg)' : 'var(--agx-surface)',
              color: orden === 'desc' ? 'var(--agx-pri2)' : 'var(--agx-muted)',
              fontSize: 11,
              fontWeight: 600,
              cursor: 'pointer',
              fontFamily: 'inherit',
            }}
          >
            Mas comprados
          </button>
          <button
            onClick={() => setOrden('asc')}
            style={{
              padding: '5px 12px',
              borderRadius: 8,
              border: '1px solid var(--agx-border)',
              background: orden === 'asc' ? 'var(--agx-blue-bg)' : 'var(--agx-surface)',
              color: orden === 'asc' ? 'var(--agx-pri2)' : 'var(--agx-muted)',
              fontSize: 11,
              fontWeight: 600,
              cursor: 'pointer',
              fontFamily: 'inherit',
            }}
          >
            Menos comprados
          </button>
        </div>
        {sorted.map((p, i) => (
          <div
            key={p.codigo || i}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              padding: '10px 0',
              borderBottom: i < sorted.length - 1 ? '1px solid var(--agx-border)' : 'none',
            }}
          >
            <div
              style={{
                width: 40,
                height: 40,
                borderRadius: 10,
                background: 'var(--agx-bg2)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              <FiPackage size={18} color="var(--agx-pri2)" />
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div
                style={{
                  fontSize: 12,
                  fontWeight: 600,
                  color: 'var(--agx-text)',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                {p.nombre || p.codigo}
              </div>
              <div style={{ fontSize: 10, color: 'var(--agx-faint)' }}>{p.codigo}</div>
            </div>
            <div style={{ textAlign: 'right' }}>
              <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--agx-pri2)' }}>
                x{p.cantidad || 0}
              </div>
              <div style={{ fontSize: 9, color: 'var(--agx-faint)' }}>unidades</div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function SyncErpModal({ cliente, onClose, onConfirm }) {
  const [q, setQ] = useState('');
  const [clientes, setClientes] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [disponible, setDisponible] = useState(true);
  const [enviando, setEnviando] = useState(null);

  const cargar = useCallback(async (busqueda) => {
    setCargando(true);
    try {
      const r = await API.get('/clientes/erp-disponibles', {
        params: { q: busqueda || '', limite: 100 },
      });
      if (r.data.status === 'success') {
        const d = r.data.data || {};
        setDisponible(!!d.disponible);
        setClientes(d.clientes || []);
      }
    } catch (e) {
      console.error(e);
      setClientes([]);
    } finally {
      setCargando(false);
    }
  }, []);

  useEffect(() => {
    const t = setTimeout(() => cargar(q), 300);
    return () => clearTimeout(t);
  }, [q, cargar]);

  const seleccionar = async (erp) => {
    setEnviando(erp.codigo);
    try {
      await onConfirm(cliente.id, erp.codigo);
    } finally {
      setEnviando(null);
    }
  };

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.5)',
        zIndex: 3000,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 20,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: '100%',
          maxWidth: 440,
          maxHeight: '80vh',
          display: 'flex',
          flexDirection: 'column',
          padding: 20,
          borderRadius: 20,
          background: 'var(--agx-surface)',
          boxShadow: '0 20px 60px rgba(0,0,0,0.2)',
        }}
      >
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: 6,
          }}
        >
          <span style={{ fontSize: 15, fontWeight: 700, color: 'var(--agx-text)' }}>
            Sincronizar con ADVIN
          </span>
          <button
            onClick={onClose}
            style={{
              width: 28,
              height: 28,
              borderRadius: '50%',
              border: 'none',
              cursor: 'pointer',
              background: 'var(--agx-bg2)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'var(--agx-faint)',
            }}
          >
            <FiX size={14} />
          </button>
        </div>
        <div style={{ fontSize: 12, color: 'var(--agx-muted)', marginBottom: 14 }}>
          Cliente: <strong style={{ color: 'var(--agx-text)' }}>{cliente.nombre}</strong>. Selecciona
          la cuenta de ADVIN a vincular.
        </div>
        <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
          <div
            style={{
              flex: 1,
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              padding: '8px 12px',
              borderRadius: 10,
              background: 'var(--agx-bg2)',
              border: '1px solid var(--agx-border)',
            }}
          >
            <FiSearch size={14} color="var(--agx-faint)" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Buscar por nombre, RIF o codigo..."
              style={{
                flex: 1,
                border: 'none',
                background: 'transparent',
                fontSize: 13,
                outline: 'none',
                color: 'var(--agx-text)',
                fontFamily: 'inherit',
              }}
            />
            {q && (
              <button onClick={() => setQ('')} style={S.clr}>
                <FiX size={12} />
              </button>
            )}
          </div>
        </div>
        <div style={{ flex: 1, overflowY: 'auto', minHeight: 120 }}>
          {cargando ? (
            <div style={S.loading}>
              <div style={S.spin} />
            </div>
          ) : !disponible ? (
            <div
              style={{
                padding: 24,
                textAlign: 'center',
                color: 'var(--agx-faint)',
                fontSize: 13,
              }}
            >
              Esta empresa no tiene sincronizacion con ADVIN.
            </div>
          ) : clientes.length === 0 ? (
            <div
              style={{
                padding: 24,
                textAlign: 'center',
                color: 'var(--agx-faint)',
                fontSize: 13,
              }}
            >
              No se encontraron clientes de ADVIN sin sincronizar.
            </div>
          ) : (
            clientes.map((c, i) => (
              <button
                key={c.codigo}
                onClick={() => seleccionar(c)}
                disabled={enviando !== null}
                style={{
                  width: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                  padding: '10px 8px',
                  border: 'none',
                  borderBottom: i < clientes.length - 1 ? '1px solid var(--agx-border)' : 'none',
                  background: 'transparent',
                  cursor: enviando !== null ? 'default' : 'pointer',
                  textAlign: 'left',
                  fontFamily: 'inherit',
                }}
              >
                <div
                  style={{
                    width: 34,
                    height: 34,
                    borderRadius: 8,
                    background: '#e0f2fe',
                    color: '#0369a1',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: 11,
                    fontWeight: 700,
                    flexShrink: 0,
                  }}
                >
                  {c.codigo.slice(0, 3)}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div
                    style={{
                      fontSize: 12,
                      fontWeight: 600,
                      color: 'var(--agx-text)',
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}
                  >
                    {c.nombre}
                  </div>
                  <div style={{ fontSize: 10, color: 'var(--agx-faint)' }}>
                    {[c.rif, c.nit, c.codigo].filter(Boolean).join(' · ')}
                  </div>
                </div>
                {enviando === c.codigo ? (
                  <div style={S.spin} />
                ) : (
                  <FiChevronRight size={16} color="var(--agx-faint)" />
                )}
              </button>
            ))
          )}
        </div>
      </div>
    </div>
  );
}

const S = {
  loading: { display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 40 },
  spin: {
    width: 24,
    height: 24,
    borderRadius: '50%',
    border: '2px solid var(--agx-border)',
    borderTop: '2px solid var(--agx-pri2)',
    animation: 'spin 0.6s linear infinite',
  },
  clr: {
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    color: 'var(--agx-faint)',
    padding: 2,
    display: 'flex',
  },
  createBtn: {
    width: 36,
    height: 36,
    borderRadius: 10,
    border: 'none',
    cursor: 'pointer',
    background: 'var(--agx-pri2)',
    color: '#fff',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  filterSel: {
    padding: '6px 10px',
    borderRadius: 8,
    border: '1px solid var(--agx-border)',
    fontSize: 11,
    background: 'var(--agx-bg2)',
    color: 'var(--agx-text)',
    fontFamily: 'inherit',
    outline: 'none',
  },
  filterBtn: {
    padding: '6px 10px',
    borderRadius: 8,
    border: '1px solid',
    fontSize: 11,
    fontWeight: 600,
    cursor: 'pointer',
    fontFamily: 'inherit',
  },
  clientRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    padding: '10px 14px',
    cursor: 'pointer',
    borderBottom: '1px solid var(--agx-border)',
  },
  backBtn: {
    width: 28,
    height: 28,
    borderRadius: 8,
    border: 'none',
    cursor: 'pointer',
    background: 'var(--agx-bg2)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: 'var(--agx-faint)',
  },
  actionBtn: {
    width: 28,
    height: 28,
    borderRadius: 8,
    border: 'none',
    cursor: 'pointer',
    background: 'var(--agx-bg2)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: 'var(--agx-faint)',
  },
  btnPri: {
    flex: 1,
    padding: '10px',
    borderRadius: 10,
    border: 'none',
    cursor: 'pointer',
    background: 'var(--agx-pri2)',
    color: '#fff',
    fontSize: 13,
    fontWeight: 600,
    fontFamily: 'inherit',
  },
  btnSec: {
    flex: 1,
    padding: '10px',
    borderRadius: 10,
    border: '1px solid var(--agx-border)',
    cursor: 'pointer',
    background: 'var(--agx-surface)',
    color: 'var(--agx-text)',
    fontSize: 13,
    fontWeight: 600,
    fontFamily: 'inherit',
  },
  btnPriS: {
    flex: 1,
    padding: '7px 14px',
    borderRadius: 8,
    border: 'none',
    cursor: 'pointer',
    background: 'var(--agx-pri2)',
    color: '#fff',
    fontSize: 11,
    fontWeight: 600,
    fontFamily: 'inherit',
  },
  btnSecS: {
    flex: 1,
    padding: '7px 14px',
    borderRadius: 8,
    border: '1px solid var(--agx-border)',
    cursor: 'pointer',
    background: 'var(--agx-surface)',
    color: 'var(--agx-text)',
    fontSize: 11,
    fontWeight: 600,
    fontFamily: 'inherit',
  },
};
